import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2$1 from 'ng-zorro-antd/popover';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import * as i3$1 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import * as i4$1 from 'ng-zorro-antd/core/transition-patch';
import * as i1 from '@va/core-library/icons';
import { exclamationTriangle, check, infoCircle, IconsModule } from '@va/core-library/icons';
import * as i3 from 'ng-zorro-antd/checkbox';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import * as i4 from 'ng-zorro-antd/radio';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import * as i5 from 'ng-zorro-antd/select';
import { NzSelectModule } from 'ng-zorro-antd/select';
import * as i6 from 'ng-zorro-antd/tooltip';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import * as i7 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import * as i8 from 'ng-zorro-antd/input-number';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import * as i9 from '@va/core-library/button';
import { ButtonModule } from '@va/core-library/button';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';

const CURRENT_YEAR = new Date().getFullYear();
const ACTUAL_CALENDAR_YEAR = CURRENT_YEAR - 1;
const REL_BASE_YEAR = 0;
const REL_MIN_YEAR_RANGE = -30;
const REL_MAX_YEAR_RANGE = 30;
const DEFAULT_FROM_YEAR = ACTUAL_CALENDAR_YEAR;
const DEFAULT_TO_YEAR = ACTUAL_CALENDAR_YEAR + 2;
const FINANCIAL_BASIS_LABEL = "FY";
const CALENDAR_BASIS_LABEL = "CY";
const PERIOD_FULL_YEAR_ID = "Y";
const ANNUAL_ID = "annual";
const MONTH_DATE_ABB_YEAR_FORMAT = "MMM Do YYYY";
const CY_PERIOD_BASIS = {
    id: "cal",
    label: "Calendar",
    shortName: CALENDAR_BASIS_LABEL
};
const FY_PERIOD_BASIS = {
    id: "fy",
    label: "Fiscal",
    shortName: FINANCIAL_BASIS_LABEL
};
const RELATIVE_BASIS_OPTION_SHORTNAME = {
    cal: {
        H: "CH",
        Q: "CQ",
        Y: CY_PERIOD_BASIS.shortName
    },
    fy: {
        H: "FH",
        Q: "FQ",
        Y: FY_PERIOD_BASIS.shortName
    }
};
const ABSOLUTE_BASIS_OPTION_SHORTNAME = {
    cal: {
        "1H": "1HCY",
        "2H": "2HCY",
        "1Q": "1QCY",
        "2Q": "2QCY",
        "3Q": "3QCY",
        "4Q": "4QCY",
        Y: CY_PERIOD_BASIS.shortName
    },
    fy: {
        "1H": "1HFY",
        "2H": "2HFY",
        "1Q": "1QFY",
        "2Q": "2QFY",
        "3Q": "3QFY",
        "4Q": "4QFY",
        Y: FY_PERIOD_BASIS.shortName
    }
};
const ABS_PERIOD_TYPE = {
    id: "abs",
    label: "Absolute",
    dataId: "absolute"
};
const REL_PERIOD_TYPE = {
    id: "rel",
    label: "Relative",
    dataId: "relative"
};
const ERROR_MESSAGE = {
    requiredPeriodOrOtherPeriod: "Must select at least 1 option.",
    similarBasis: "Period types must match, e.g. both annuals or interims",
    interimAndOther: "Rolling periods (LTM/NTM/STM) are only applicable for annuals.",
    requiredBasis: "Must select basis value."
};
const DEFAULT_SINGLE_PERIOD_FILTER_DATA = {
    is_annual_selected: true,
    period_basis: {
        abs: {
            from_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                },
                {
                    label: "1Q",
                    name: "1Q",
                    selected: false
                },
                {
                    label: "2Q",
                    name: "2Q",
                    selected: false
                },
                {
                    label: "3Q",
                    name: "3Q",
                    selected: false
                },
                {
                    label: "4Q",
                    name: "4Q",
                    selected: false
                },
                {
                    label: "1H",
                    name: "1H",
                    selected: false
                },
                {
                    label: "2H",
                    name: "2H",
                    selected: false
                }
            ],
            to_basis: undefined
        },
        rel: {
            from_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                },
                {
                    label: "Q",
                    name: "Q",
                    selected: false
                },
                {
                    label: "H",
                    name: "H",
                    selected: false
                }
            ],
            to_basis: undefined
        }
    },
    basis: [
        {
            label: "Calendar",
            name: "cal",
            selected: true
        },
        {
            label: "Fiscal",
            name: "fy",
            selected: false
        }
    ],
    from_to: {
        selected_from: CURRENT_YEAR
    },
    other_period: [
        {
            selected: false,
            label: "LTM",
            name: "ltm",
            period_list: ["LTM"]
        },
        {
            selected: false,
            label: "NTM",
            name: "ntm",
            period_list: ["NTM"]
        },
        {
            selected: false,
            label: "STM",
            name: "stm",
            period_list: ["STM"]
        }
    ],
    type: [
        {
            label: "Absolute",
            name: "abs",
            selected: true
        },
        {
            label: "Relative",
            name: "rel",
            selected: false
        }
    ]
};
const DEFAULT_RANGE_PERIOD_FILTER_DATA = {
    is_annual_selected: true,
    period_basis: {
        abs: {
            from_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                },
                {
                    label: "1Q",
                    name: "1Q",
                    selected: false
                },
                {
                    label: "2Q",
                    name: "2Q",
                    selected: false
                },
                {
                    label: "3Q",
                    name: "3Q",
                    selected: false
                },
                {
                    label: "4Q",
                    name: "4Q",
                    selected: false
                },
                {
                    label: "1H",
                    name: "1H",
                    selected: false
                },
                {
                    label: "2H",
                    name: "2H",
                    selected: false
                }
            ],
            to_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                },
                {
                    label: "1Q",
                    name: "1Q",
                    selected: false
                },
                {
                    label: "2Q",
                    name: "2Q",
                    selected: false
                },
                {
                    label: "3Q",
                    name: "3Q",
                    selected: false
                },
                {
                    label: "4Q",
                    name: "4Q",
                    selected: false
                },
                {
                    label: "1H",
                    name: "1H",
                    selected: false
                },
                {
                    label: "2H",
                    name: "2H",
                    selected: false
                }
            ]
        },
        rel: {
            from_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                },
                {
                    label: "Q",
                    name: "Q",
                    selected: false
                },
                {
                    label: "H",
                    name: "H",
                    selected: false
                }
            ],
            to_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                },
                {
                    label: "Q",
                    name: "Q",
                    selected: false
                },
                {
                    label: "H",
                    name: "H",
                    selected: false
                }
            ]
        }
    },
    basis: [
        {
            label: "Calendar",
            name: "cal",
            selected: true
        },
        {
            label: "Fiscal",
            name: "fy",
            selected: false
        }
    ],
    from_to: {
        selected_from: CURRENT_YEAR - 2,
        selected_to: CURRENT_YEAR + 3
    },
    other_period: [
        {
            selected: false,
            label: "LTM",
            name: "ltm",
            period_list: ["LTM"]
        },
        {
            selected: false,
            label: "NTM",
            name: "ntm",
            period_list: ["NTM"]
        },
        {
            selected: false,
            label: "STM",
            name: "stm",
            period_list: ["STM"]
        }
    ],
    type: [
        {
            label: "Absolute",
            name: "abs",
            selected: true
        },
        {
            label: "Relative",
            name: "rel",
            selected: false
        }
    ]
};
const DEFAULT_VALUATION_PERIOD_FILTER_DATA = {
    is_annual_selected: true,
    period_basis: {
        abs: {
            from_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: false
                }
            ],
            to_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: false
                }
            ]
        },
        rel: {
            from_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                }
            ],
            to_basis: [
                {
                    label: "",
                    name: "Y",
                    selected: true
                }
            ]
        }
    },
    basis: [
        {
            label: "Calendar",
            name: "cal",
            selected: true
        },
        {
            label: "Fiscal",
            name: "fy",
            selected: false
        }
    ],
    from_to: {
        selected_from: CURRENT_YEAR - 2,
        selected_to: CURRENT_YEAR + 3
    },
    other_period: [
        {
            selected: false,
            label: "LTM",
            name: "ltm",
            period_list: ["LTM"]
        },
        {
            selected: true,
            label: "NTM",
            name: "ntm",
            period_list: ["NTM"]
        },
        {
            selected: false,
            label: "STM",
            name: "stm",
            period_list: ["STM"]
        }
    ],
    type: [
        {
            label: "Absolute",
            name: "abs",
            selected: true
        },
        {
            label: "Relative",
            name: "rel",
            selected: false
        }
    ]
};

/**
 * Determines if the selected period type is absolute or not
 * @param periodType - periods.type array that we get from meta data
 * @returns true/false implying period type ais absolute or not
 */
function getIsAbsolutePeriod(periodType) {
    return (periodType.find((item) => item.name === ABS_PERIOD_TYPE.id) || {})
        .selected;
}
/**
 * Determines if the selected period basis is calendar or not
 * @param periodBasis - periods.basis array that we get from meta data
 * @returns true/false implying period basis ais cal or not
 */
function getIsCalYearSelected(periodBasis) {
    return (periodBasis.find((item) => item.name === CY_PERIOD_BASIS.id) || {})
        .selected;
}
/**
 * Determines selected period type
 * @param periodType - periods.type array that we get from meta data
 * @returns selected period type [abs | rel]
 */
function getSelectedPeriodType(periodType) {
    const isAbsolutePeriod = getIsAbsolutePeriod(periodType);
    return isAbsolutePeriod ? ABS_PERIOD_TYPE.id : REL_PERIOD_TYPE.id;
}
/**
 * Determines selected period basis
 * @param periodBasis - periods.basis array that we get from meta data
 * @returns selected period type [cal | fy]
 */
function getSelectedPeriodBasis(periodBasis) {
    const isCalYear = getIsCalYearSelected(periodBasis);
    return isCalYear ? CY_PERIOD_BASIS.id : FY_PERIOD_BASIS.id;
}
/**
 * It is used to get the prefix according selected period basis
 * @param isCyYear indicates if selected year basis is Calendar or not
 * @returns period basis prefix
 */
function getBasisPrefix(isCalYear) {
    return isCalYear ? CY_PERIOD_BASIS.shortName : FY_PERIOD_BASIS.shortName;
}
/**
 * Determines if the selected basis period is Full year i.e. 'Y' or not
 * @param selectedBasisOption selected basis option (Y|Q|H|1Q|2Q|3Q|4Q)
 * @returns returns true if basis id is 'Y'
 */
function isBasisFullYear(selectedBasisOption) {
    return selectedBasisOption === PERIOD_FULL_YEAR_ID;
}
/**
 * Determines if the selected basis period is quarterly i.e. Q|1Q|2Q|3Q|4Q  or not
 * @param selectedBasisOption selected basis option (Y|Q|H|1Q|2Q|3Q|4Q)
 * @returns returns true if basis id is Q|1Q|2Q|3Q|4Q
 */
function isBasisQuarterly(selectedBasisOption) {
    return (selectedBasisOption === "1Q" ||
        selectedBasisOption === "2Q" ||
        selectedBasisOption === "3Q" ||
        selectedBasisOption === "4Q" ||
        selectedBasisOption === "Q");
}
/**
 * Determines if the selected basis period is half yearly i.e. H|1H|2H  or not
 * @param selectedBasisOption selected basis option (Y|Q|H|1Q|2Q|3Q|4Q)
 * @returns returns true if basis id is H|1H|2H
 */
function isBasisHalfYearly(selectedBasisOption) {
    return (selectedBasisOption === "1H" ||
        selectedBasisOption === "2H" ||
        selectedBasisOption === "H");
}
function getPeriodMinMax(periodMinMax) {
    let min = periodMinMax && periodMinMax.min !== null && periodMinMax.min !== undefined
        ? periodMinMax.min
        : REL_MIN_YEAR_RANGE;
    let max = periodMinMax && periodMinMax.max !== null && periodMinMax.max !== undefined
        ? periodMinMax.max
        : REL_MAX_YEAR_RANGE;
    return { min, max };
}
/**
 * Determines the min-max range of year in case of relative period
 * It is -30 to +30 for Full year relative period
 * It is -124 to +124 for quarterly relative period
 * It is -61 to +61 for half yearly relative period
 * @param selectedBasisOption selected basis option (Y|Q|H)
 * @returns min-max relative year range
 */
function getRelativeMinMAxValue(selectedBasisOption, periodMinMax) {
    const minMax = getPeriodMinMax(periodMinMax);
    const minValue = minMax.min;
    const maxValue = minMax.max;
    const absMinMax = getAbsoluteMinMaxValue(periodMinMax);
    const isFullYear = isBasisFullYear(selectedBasisOption);
    if (isFullYear) {
        return { min: minValue, max: maxValue };
    }
    const isQuarterly = isBasisQuarterly(selectedBasisOption);
    if (isQuarterly) {
        // return {
        //   min: (minValue-1) * 4,
        //   max: (maxValue+1) * 4
        // };
        // why 1Q and 4Q are hardcoded here? Because, We are calculating min and max for the text field
        // for a given year. So for a given year minimum is first quarter and maximum is 4th quarter.
        return {
            min: convertAbsToRel(absMinMax.min, "1Q").year,
            max: convertAbsToRel(absMinMax.max, "4Q").year
        };
    }
    const isHalfYearly = isBasisHalfYearly(selectedBasisOption);
    if (isHalfYearly) {
        // return {
        //   min: (minValue-1)*2,
        //   max: (maxValue+1)*2
        // };
        // why 1H and 2H are hardcoded here? Because, We are calculating min and max for the text field
        // for a given year. So for a given year minimum is first half and maximum is 4th half
        return {
            min: convertAbsToRel(absMinMax.min, "1H").year,
            max: convertAbsToRel(absMinMax.max, "2H").year
        };
    }
    return { min: minValue, max: maxValue };
}
function getAbsoluteMinMaxValue(periodMinMax) {
    const minMax = getPeriodMinMax(periodMinMax);
    return {
        min: ACTUAL_CALENDAR_YEAR + minMax.min,
        max: ACTUAL_CALENDAR_YEAR + minMax.max
    };
}
/**
 * It is used to get min and max value of year
 * It formats it according to selected period type
 * It is -30 to +30 for all absolute period
 * It is -30 to +30 for Full year relative period
 * It is -124 to +124 for quarterly relative period
 * It is -61 to +61 for half yearly relative period
 * @param isAbsType indicates if selected year type is Absolute or not
 * @param selectedBasisOption selected basis option (Y|Q|H|1Q|2Q|3Q|4Q)
 * @returns min, max value of year for Number Input component
 */
function getMinMAxYearRange(isAbsType, selectedBasisOption, periodMinMax) {
    if (isAbsType) {
        return getAbsoluteMinMaxValue(periodMinMax);
    }
    const { min: relativeMin, max: relativeMax } = getRelativeMinMAxValue(selectedBasisOption, periodMinMax);
    return {
        min: relativeMin,
        max: relativeMax
    };
}
/**
 * This method gives the array of names of selected items
 * @param arr - array of element which has selected and name property
 * @returns arr of selected names
 */
function getNameArrAccordingToSelectedState(arr) {
    if (arr && Array.isArray(arr)) {
        return arr.reduce((previousValue, currentValue) => currentValue.selected
            ? previousValue.concat(currentValue.name)
            : previousValue, []);
    }
    return undefined;
}
/**
 * Checks if year value exist or not
 * @param value: period value
 * @return true/false
 */
function periodYearValueExist(value) {
    return value !== "" && value !== undefined && typeof value === "number";
}
/**
 * Checks if period from-to basis belongs to the same group
 * @param fromBasis: from basis value
 * @param toBasis: to basis value
 * @return true/false
 */
function isPeriodFromToBasisDifferent(fromBasis, toBasis) {
    const isQuarterSelected = isBasisQuarterly(fromBasis);
    const halfYearSelected = isBasisHalfYearly(fromBasis);
    const isFullYearSelected = isBasisFullYear(fromBasis);
    const isOtherBasisQuarterly = isBasisQuarterly(toBasis);
    const isOtherBasisHalfYearly = isBasisHalfYearly(toBasis);
    const isOtherBasisYearly = isBasisFullYear(toBasis);
    return ((isFullYearSelected && !isOtherBasisYearly) ||
        (halfYearSelected && !isOtherBasisHalfYearly) ||
        (isQuarterSelected && !isOtherBasisQuarterly));
}
/**
 * Creates formatted label for basis option list in case of absolute period type.
 * The label adds postfix according to selected basis type
 * @param optionList: basis option list
 * @param periodTypeBasisValue: selected period type option value
 * @param basisType: type of basis radio
 * @return formatted option list
 */
function formatAbsolutePeriodBasisOptionList(optionList, periodTypeBasisValue, basisType) {
    if (optionList && Array.isArray(optionList)) {
        return optionList.map((item) => ({
            ...item,
            selected: item.name === periodTypeBasisValue,
            formattedLabel: ABSOLUTE_BASIS_OPTION_SHORTNAME[basisType][item.name]
        }));
    }
    return undefined;
}
/**
 * Creates formatted label for basis option list in case of relative period type.
 * The label adds postfix according to selected basis type
 * @param optionList: basis option list
 * @param periodTypeBasisValue: selected period type option value
 * @param basisType: type of basis radio
 * @return formatted option list
 */
function formatRelativePeriodBasisOptionList(optionList, periodTypeBasisValue, basisType) {
    if (optionList && Array.isArray(optionList)) {
        return optionList.map((item) => {
            return {
                ...item,
                selected: item.name === periodTypeBasisValue,
                formattedLabel: RELATIVE_BASIS_OPTION_SHORTNAME[basisType][item.name]
            };
        });
    }
    return undefined;
}
/**
 * Determines selected period basis option from given option list
 * @param basisOptionList: basis options list
 * @return selected basis
 */
function getSelectedBasisOption(basisOptionList) {
    let selectedBasisOption = null;
    if (basisOptionList) {
        selectedBasisOption = basisOptionList.find((item) => item.selected);
    }
    return selectedBasisOption;
}
/**
 * Determines id of selected period basis option from given option list
 * @param basisOptionList: basis options list
 * @return selected basis id
 */
function getSelectedBasisOptionId(basisOptionList) {
    let selectedBasisOption = null;
    let selectedBasisOptionId = null;
    if (basisOptionList) {
        selectedBasisOption = basisOptionList.find((item) => item.selected);
    }
    if (selectedBasisOption) {
        selectedBasisOptionId = selectedBasisOption.name;
    }
    return selectedBasisOptionId;
}
/**
 * Determines if given id is absolute type of id
 * @param id: (abs|rel)
 * @return true if it is absolute type id
 */
function getIsAbsolutePeriodTypeId(id) {
    return id === ABS_PERIOD_TYPE.id;
}
/**
 * Determines if given id is calendar basis
 * @param id: (cal|fy)
 * @return true if it is calendar basis id
 */
function getIsCalendarBasisId(id) {
    return id === CY_PERIOD_BASIS.id;
}
/**
 * Determines if it is multi period select filter
 * @param periodMeta: period that we get from meta calls
 * @return true if it is multi period select filter
 */
function getIsMultiSelectPeriodFilter(periodMeta) {
    return (periodMeta.from_to.selected_to !== undefined &&
        periodMeta.from_to.selected_to !== null);
}
/**
 * It is used to create label value for period filter
 * @returns formatted string of selected filters
 */
function createSelectedPeriodLabel(periodDataInput) {
    const periodData = { ...periodDataInput };
    const isMultiSelectPeriodFilter = getIsMultiSelectPeriodFilter(periodData);
    const isAnnualSelected = periodData.is_annual_selected;
    if (isAnnualSelected && !isMultiSelectPeriodFilter) {
        periodData.other_period = periodData.other_period.map((item) => ({ ...item, selected: false }));
    }
    let result = "";
    let fromLabel = "";
    let toLabel = "";
    let fromBasis = "";
    let toBasis = "";
    const fromYear = periodData.from_to.selected_from;
    const toYear = periodData.from_to.selected_to;
    const isAbsType = getIsAbsolutePeriod(periodData.type);
    const selectedPeriodType = getSelectedPeriodType(periodData.type);
    const selectedBasis = getSelectedPeriodBasis(periodData.basis);
    const selectedFromBasisOption = periodData.period_basis &&
        periodData.period_basis[selectedPeriodType] &&
        periodData.period_basis[selectedPeriodType].from_basis &&
        periodData.period_basis[selectedPeriodType].from_basis.length &&
        getSelectedBasisOption(periodData.period_basis[selectedPeriodType].from_basis);
    const periodTypeFromBasis = selectedFromBasisOption
        ? selectedFromBasisOption.name
        : PERIOD_FULL_YEAR_ID;
    const selectedToBasisOption = periodData.period_basis &&
        periodData.period_basis[selectedPeriodType] &&
        periodData.period_basis[selectedPeriodType].to_basis &&
        periodData.period_basis[selectedPeriodType].to_basis.length &&
        getSelectedBasisOption(periodData.period_basis[selectedPeriodType].to_basis);
    const periodTypeToBasis = selectedToBasisOption
        ? selectedToBasisOption.name
        : PERIOD_FULL_YEAR_ID;
    if (periodData.is_annual_selected) {
        if (fromYear !== undefined && fromYear !== null) {
            fromLabel = `${fromYear}`;
        }
        if (toYear !== undefined && toYear !== null) {
            toLabel = `${toYear}`;
        }
        fromBasis = isAbsType
            ? ABSOLUTE_BASIS_OPTION_SHORTNAME[selectedBasis][periodTypeFromBasis]
            : RELATIVE_BASIS_OPTION_SHORTNAME[selectedBasis][periodTypeFromBasis];
        if (fromLabel) {
            result += `${fromBasis || ""} ${fromLabel}`;
        }
        toBasis = isAbsType
            ? ABSOLUTE_BASIS_OPTION_SHORTNAME[selectedBasis][periodTypeToBasis]
            : RELATIVE_BASIS_OPTION_SHORTNAME[selectedBasis][periodTypeToBasis];
        if (fromLabel && toLabel) {
            result += ` - ${toBasis || ""} ${toLabel}`;
        }
    }
    const otherItems = [];
    for (const item of periodData.other_period) {
        if (item.selected) {
            otherItems.push(item.label);
        }
    }
    result += `${result && otherItems && otherItems.length ? ", " : ""}${otherItems.join(", ")}`;
    return result;
}
/**
 * Determines the number of months between given duration
 * @param dateTo: to date
 * @param dateFrom: from date
 * @return months difference
 */
function getMonthsDifference(dateTo, dateFrom) {
    return (dateTo.getMonth() -
        dateFrom.getMonth() +
        12 * (dateTo.getFullYear() - dateFrom.getFullYear()));
}
/**
 * Determines quarter index according to month
 * @param monthIndex: month index (0-11)
 * @return quarter index (1 | 2 | 3 | 4)
 */
function getQuarterAccToMonth(monthIndex) {
    if (monthIndex < 3) {
        return 1;
    }
    if (monthIndex < 6) {
        return 2;
    }
    if (monthIndex < 9) {
        return 3;
    }
    return 4;
}
/**
 * Determines half-year index according to month
 * @param monthIndex: month index (0-11)
 * @return half year index (1 | 2)
 */
function getHalfYearAccToMonth(monthIndex) {
    if (monthIndex < 6) {
        return 1;
    }
    return 2;
}
/**
 * Converts relative year, basis to absolute year, basis
 * @param year: relative year
 * @param basis: relative basis (Q | H | Y)
 * @return absolute year and basis
 */
function convertRelToAbs(year, basis) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    let resultYear = null;
    let resultBasis = null;
    if (basis === "Q") {
        const currentQuarter = getQuarterAccToMonth(currentMonth); //2
        const lastCompletedMonthQuarterMapping = {
            1: {
                month: 9,
                year: currentYear - 1
            },
            2: {
                month: 0,
                year: currentYear
            },
            3: {
                month: 3,
                year: currentYear
            },
            4: {
                month: 6,
                year: currentYear
            }
        };
        const lastCompletedQuarterDate = new Date(lastCompletedMonthQuarterMapping[currentQuarter].year, lastCompletedMonthQuarterMapping[currentQuarter].month);
        lastCompletedQuarterDate.setMonth(lastCompletedQuarterDate.getMonth() + year * 3);
        resultYear = lastCompletedQuarterDate.getFullYear();
        resultBasis = `${getQuarterAccToMonth(lastCompletedQuarterDate.getMonth())}Q`;
    }
    else if (basis === "H") {
        const currentHalfYear = getHalfYearAccToMonth(currentMonth);
        const actualMonthHalfYearMapping = {
            1: {
                month: 6,
                year: currentYear - 1
            },
            2: {
                month: 0,
                year: currentYear
            }
        };
        const lastCompletedHalfYearDate = new Date(actualMonthHalfYearMapping[currentHalfYear].year, actualMonthHalfYearMapping[currentHalfYear].month);
        lastCompletedHalfYearDate.setMonth(lastCompletedHalfYearDate.getMonth() + year * 6);
        resultYear = lastCompletedHalfYearDate.getFullYear();
        resultBasis = `${getHalfYearAccToMonth(lastCompletedHalfYearDate.getMonth())}H`;
    }
    else {
        const actualYear = currentYear - 1;
        resultYear = actualYear + year;
        resultBasis = PERIOD_FULL_YEAR_ID;
    }
    return {
        year: resultYear,
        basis: resultBasis
    };
}
/**
 * Converts absolute year, basis to relative year, basis
 * @param year: absolute year
 * @param basis: absolute basis (1Q | 2Q | 3Q | 4Q | 1H | 2H | Y)
 * @return relative year and basis
 */
function convertAbsToRel(year, basis) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    let resultYear = null;
    let resultBasis = null;
    if (basis === "1Q" || basis === "2Q" || basis === "3Q" || basis === "4Q") {
        const monthQuarterMapping = {
            1: 0,
            2: 3,
            3: 6,
            4: 9
        };
        const lastCompletedMonthQuarterMapping = {
            1: {
                month: 9,
                year: currentYear - 1
            },
            2: {
                month: 0,
                year: currentYear
            },
            3: {
                month: 3,
                year: currentYear
            },
            4: {
                month: 6,
                year: currentYear
            }
        };
        const givenQuarterNumber = parseInt(basis); //1
        const givenQuarterDate = new Date(year, monthQuarterMapping[givenQuarterNumber], 1);
        const currentQuarter = getQuarterAccToMonth(currentMonth);
        const lastCompletedQuarterDate = new Date(lastCompletedMonthQuarterMapping[currentQuarter].year, lastCompletedMonthQuarterMapping[currentQuarter].month);
        let fromDate = null;
        let toDate = null;
        const isGivenDateLessThanCurrentDate = givenQuarterDate < lastCompletedQuarterDate;
        if (isGivenDateLessThanCurrentDate) {
            fromDate = givenQuarterDate;
            toDate = lastCompletedQuarterDate;
        }
        else {
            fromDate = lastCompletedQuarterDate;
            toDate = givenQuarterDate;
        }
        const monthDiffBetweenQuarters = getMonthsDifference(toDate, fromDate);
        resultYear = isGivenDateLessThanCurrentDate
            ? (monthDiffBetweenQuarters / 3) * -1
            : monthDiffBetweenQuarters / 3;
        resultBasis = "Q";
    }
    else if (basis === "1H" || basis === "2H") {
        const halfYearMapping = {
            1: 0,
            2: 6
        };
        const lastCompletedMonthHalfYearMapping = {
            1: {
                month: 6,
                year: currentYear - 1
            },
            2: {
                month: 0,
                year: currentYear
            }
        };
        const givenHalfYearNumber = parseInt(basis);
        const givenHalfYearDate = new Date(year, halfYearMapping[givenHalfYearNumber]);
        const currentHalfYear = getHalfYearAccToMonth(currentMonth);
        const lastCompletedHalfYearDate = new Date(lastCompletedMonthHalfYearMapping[currentHalfYear].year, lastCompletedMonthHalfYearMapping[currentHalfYear].month);
        let fromDate = null;
        let toDate = null;
        const isGivenDateLessThanCurrentDate = givenHalfYearDate < lastCompletedHalfYearDate;
        if (isGivenDateLessThanCurrentDate) {
            fromDate = givenHalfYearDate;
            toDate = lastCompletedHalfYearDate;
        }
        else {
            fromDate = lastCompletedHalfYearDate;
            toDate = givenHalfYearDate;
        }
        const monthDiffBetweenHalfYears = getMonthsDifference(toDate, fromDate);
        resultYear = isGivenDateLessThanCurrentDate
            ? (monthDiffBetweenHalfYears / 6) * -1
            : monthDiffBetweenHalfYears / 6;
        resultBasis = "H";
    }
    else {
        const actualYear = currentYear - 1;
        const diff = Math.abs(actualYear - year);
        resultYear = year < actualYear ? diff * -1 : diff;
        resultBasis = PERIOD_FULL_YEAR_ID;
    }
    return {
        year: resultYear,
        basis: resultBasis
    };
}
// user settings period object
/**
 * Convert period meta object to the shape which is required for user settings.
 * @param periodMeta: period that we get from meta calls
 * @return user settings period object
 */
function convertPeriodMetaToUserSettings(periodMeta) {
    const periodType = getSelectedPeriodType(periodMeta.type);
    const periodBasis = getSelectedPeriodBasis(periodMeta.basis);
    const periodOther = getNameArrAccordingToSelectedState(periodMeta.other_period);
    let periodFrom = periodMeta.from_to.selected_from;
    let periodTypeFromBasis = periodMeta.period_basis &&
        periodMeta.period_basis[periodType] &&
        periodMeta.period_basis[periodType] &&
        periodMeta.period_basis[periodType].from_basis &&
        periodMeta.period_basis[periodType].from_basis.length
        ? getSelectedBasisOptionId(periodMeta.period_basis[periodType].from_basis)
        : PERIOD_FULL_YEAR_ID;
    const periodToExist = periodYearValueExist(periodMeta.from_to.selected_to);
    let periodTo = periodToExist ? periodMeta.from_to.selected_to : undefined;
    let periodTypeToBasis = periodToExist &&
        periodMeta.period_basis &&
        periodMeta.period_basis[periodType] &&
        periodMeta.period_basis[periodType] &&
        periodMeta.period_basis[periodType].to_basis &&
        periodMeta.period_basis[periodType].to_basis.length
        ? getSelectedBasisOptionId(periodMeta.period_basis[periodType].to_basis) || PERIOD_FULL_YEAR_ID
        : undefined;
    if (periodType === ABS_PERIOD_TYPE.id) {
        const fromBasisData = convertAbsToRel(periodMeta.from_to.selected_from, periodTypeFromBasis);
        periodFrom = fromBasisData.year;
        periodTypeFromBasis = fromBasisData.basis;
        if (periodTo) {
            const toBasisData = convertAbsToRel(periodTo, periodTypeToBasis);
            periodTo = toBasisData.year;
            periodTypeToBasis = toBasisData.basis;
        }
    }
    const periodUserSettingData = {
        type: periodType,
        basis: periodBasis,
        from: periodFrom,
        to: periodTo,
        other: periodOther,
        period_type_basis_from: periodTypeFromBasis || PERIOD_FULL_YEAR_ID,
        period_type_basis_to: periodTypeToBasis,
        is_annual_selected: periodMeta.is_annual_selected
    };
    return periodUserSettingData;
}

/**
 * This component represents "period filter content"
 * This component allows user to select period type, basis, from-to year range and other period(NTM, STM, LTM)
 */
class PeriodFilterContentComponent {
    constructor(iconRegistryService) {
        this.iconRegistryService = iconRegistryService;
        this.periodData = DEFAULT_SINGLE_PERIOD_FILTER_DATA;
        this.periodMinMax = { min: REL_MIN_YEAR_RANGE, max: REL_MAX_YEAR_RANGE };
        this.hidePeriodType = false;
        this.hidePeriodBasis = false;
        this.showApplyBtn = true;
        this.onApply = new EventEmitter();
        this.onError = new EventEmitter();
        // data
        this.dataInfo = {};
        this.periodFrom = DEFAULT_FROM_YEAR;
        this.periodTo = DEFAULT_TO_YEAR;
        this.periodFromBasisOptionValue = PERIOD_FULL_YEAR_ID;
        this.periodToBasisOptionValue = PERIOD_FULL_YEAR_ID;
        this.minValue = 0;
        this.maxValue = 0;
        this.basisLabel = CALENDAR_BASIS_LABEL;
        this.hasClickedApply = false;
        this.isPeriodFromYearInvalid = false;
        this.isPeriodToYearInvalid = false;
        this.errorMessage = "";
        this.isMultiSelectPeriodFilter = true;
        this.hasOnlyYearBasis = true;
        this.hasOtherPeriodData = false;
        this.annulId = ANNUAL_ID;
        this.isAnnualSelected = true;
        this.annualLabel = "Annual / Interim";
        this.iconRegistryService.registerIcons([exclamationTriangle, check, infoCircle]);
    }
    ngOnInit() {
        if (this.periodData &&
            this.periodData.type &&
            this.periodData.basis &&
            this.periodData.from_to) {
            this.setModelValues();
            this.checkValidation();
            this.setDataInfoMsg();
        }
    }
    ngOnChanges(changes) {
        if (changes.periodData &&
            changes.periodData.currentValue &&
            changes.periodData.currentValue !== changes.periodData.previousValue) {
            // set model variables
            if (this.periodData.type &&
                this.periodData.basis &&
                this.periodData.from_to) {
                this.setModelValues();
                this.checkValidation();
                this.setDataInfoMsg();
            }
        }
        if (changes.dataAvailable &&
            changes.dataAvailable.currentValue !== changes.dataAvailable.previousValue) {
            this.setDataInfoMsg();
        }
    }
    setDataInfoMsg() {
        let param;
        const isCalYear = getIsCalendarBasisId(this.periodBasisValue);
        if (this.dataAvailable) {
            if (isCalYear) {
                param = this.dataAvailable.cy_abs;
            }
            else {
                param = this.dataAvailable.fy_abs;
            }
        }
        this.dataInfo = {
            from: param && param.from,
            to: param && param.to
        };
    }
    checkValidation() {
        // check if period from-to value exist or not and update error state
        const periodFromExist = periodYearValueExist(this.periodFrom);
        if (periodFromExist && this.isPeriodFromYearInvalid) {
            this.isPeriodFromYearInvalid = false;
        }
        else if (!periodFromExist && !this.isPeriodFromYearInvalid) {
            this.isPeriodFromYearInvalid = true;
        }
        const periodToExist = periodYearValueExist(this.periodTo);
        if (periodToExist && this.isPeriodToYearInvalid) {
            this.isPeriodToYearInvalid = false;
        }
        else if (this.isMultiSelectPeriodFilter &&
            !periodToExist &&
            !this.isPeriodToYearInvalid) {
            this.isPeriodToYearInvalid = true;
        }
    }
    /**
     * Executes on change of period state
     * It sets ngModel values of the radio, checkbox and number input component
     * It creates the label for selected values and displays it
     */
    setModelValues() {
        this.isMultiSelectPeriodFilter = getIsMultiSelectPeriodFilter(this.periodData);
        this.hasOtherPeriodData =
            this.periodData.other_period && !!this.periodData.other_period.length;
        this.periodTypeValue = getSelectedPeriodType(this.periodData.type);
        this.periodBasisValue = getSelectedPeriodBasis(this.periodData.basis);
        const isCalYear = getIsCalendarBasisId(this.periodBasisValue);
        this.basisLabel = getBasisPrefix(isCalYear);
        if (this.hasOtherPeriodData) {
            this.isAnnualSelected = this.periodData.is_annual_selected;
            if (this.isAnnualSelected && !this.isMultiSelectPeriodFilter) {
                this.periodData.other_period = this.periodData.other_period.map((item) => ({ ...item, selected: false }));
            }
            this.periodOtherValue = getNameArrAccordingToSelectedState(this.periodData.other_period);
            if (!this.isAnnualSelected && this.periodOtherValue) {
                this.otherPeriodRadioValue = this.periodOtherValue[0];
            }
            else {
                this.otherPeriodRadioValue = null;
            }
        }
        this.periodFrom = this.periodData.from_to
            ? this.periodData.from_to.selected_from
            : undefined;
        this.periodTo = this.periodData.from_to
            ? this.periodData.from_to.selected_to
            : undefined;
        const isAbsType = getIsAbsolutePeriodTypeId(this.periodTypeValue);
        this.hasOnlyYearBasis =
            this.periodData.period_basis &&
                this.periodData.period_basis.abs.from_basis.length === 1 &&
                this.periodData.period_basis.abs.from_basis[0].name ===
                    PERIOD_FULL_YEAR_ID;
        if (this.hasOnlyYearBasis) {
            this.annualLabel = "Annual";
        }
        if (this.periodData.period_basis &&
            this.periodData.period_basis[this.periodTypeValue]) {
            this.periodFromBasisOptionValue =
                getSelectedBasisOptionId(this.periodData.period_basis[this.periodTypeValue].from_basis) || PERIOD_FULL_YEAR_ID;
            this.periodToBasisOptionValue =
                getSelectedBasisOptionId(this.periodData.period_basis[this.periodTypeValue].to_basis) || PERIOD_FULL_YEAR_ID;
        }
        let min = REL_MIN_YEAR_RANGE;
        let max = REL_MAX_YEAR_RANGE;
        if (this.periodData.period_basis.abs &&
            this.periodData.period_basis.abs.from_basis &&
            this.periodData.period_basis.abs.from_basis.length) {
            this.absolutePeriodFromBasisOptionList = formatAbsolutePeriodBasisOptionList(this.periodData.period_basis.abs.from_basis, this.periodFromBasisOptionValue, this.periodBasisValue);
            const minMaxRange = getMinMAxYearRange(isAbsType, this.periodFromBasisOptionValue, this.periodMinMax);
            min = minMaxRange.min;
            max = minMaxRange.max;
        }
        if (this.periodData.period_basis.rel &&
            this.periodData.period_basis.rel.from_basis &&
            this.periodData.period_basis.rel.from_basis.length) {
            this.relativePeriodFromBasisOptionList = formatRelativePeriodBasisOptionList(this.periodData.period_basis.rel.from_basis, this.periodFromBasisOptionValue, this.periodBasisValue);
        }
        if (this.periodData.period_basis.abs &&
            this.periodData.period_basis.abs.to_basis &&
            this.periodData.period_basis.abs.to_basis.length) {
            this.absolutePeriodToBasisOptionList = formatAbsolutePeriodBasisOptionList(this.periodData.period_basis.abs.to_basis, this.periodToBasisOptionValue, this.periodBasisValue);
        }
        if (this.periodData.period_basis.rel &&
            this.periodData.period_basis.rel.to_basis &&
            this.periodData.period_basis.rel.to_basis.length) {
            this.relativePeriodToBasisOptionList = formatRelativePeriodBasisOptionList(this.periodData.period_basis.rel.to_basis, this.periodToBasisOptionValue, this.periodBasisValue);
        }
        this.minValue = min;
        this.maxValue = max;
    }
    /**
     * Executes on period type radio value change
     * According to selected period type, it changes absolute value to relative and vice versa.
     * It updates minValue, maxValue, fromValue, toValue according to period type.
     * @param value selected period type
     */
    onPeriodTypeChange(value) {
        const isAbsType = getIsAbsolutePeriodTypeId(value);
        if (periodYearValueExist(this.periodFrom)) {
            let yearBasisData = null;
            if (isAbsType) {
                yearBasisData = convertRelToAbs(this.periodFrom, this.periodFromBasisOptionValue);
            }
            else {
                yearBasisData = convertAbsToRel(this.periodFrom, this.periodFromBasisOptionValue);
            }
            this.periodFrom = yearBasisData.year;
            this.periodFromBasisOptionValue = yearBasisData.basis;
        }
        if (periodYearValueExist(this.periodTo)) {
            let yearBasisData = null;
            if (isAbsType) {
                yearBasisData = convertRelToAbs(this.periodTo, this.periodToBasisOptionValue);
            }
            else {
                yearBasisData = convertAbsToRel(this.periodTo, this.periodToBasisOptionValue);
            }
            this.periodTo = yearBasisData.year;
            this.periodToBasisOptionValue = yearBasisData.basis;
        }
        const { min, max } = getMinMAxYearRange(isAbsType, this.periodFromBasisOptionValue, this.periodMinMax);
        this.minValue = min;
        this.maxValue = max;
        this.absolutePeriodFromBasisOptionList = formatAbsolutePeriodBasisOptionList(this.absolutePeriodFromBasisOptionList, this.periodFromBasisOptionValue, this.periodBasisValue);
        this.relativePeriodFromBasisOptionList = formatRelativePeriodBasisOptionList(this.relativePeriodFromBasisOptionList, this.periodFromBasisOptionValue, this.periodBasisValue);
        if (this.absolutePeriodToBasisOptionList) {
            this.absolutePeriodToBasisOptionList = formatAbsolutePeriodBasisOptionList(this.absolutePeriodToBasisOptionList, this.periodToBasisOptionValue, this.periodBasisValue);
        }
        if (this.relativePeriodToBasisOptionList) {
            this.relativePeriodToBasisOptionList = formatRelativePeriodBasisOptionList(this.relativePeriodToBasisOptionList, this.periodToBasisOptionValue, this.periodBasisValue);
        }
    }
    /**
     * Executes on period basis radio value change
     * According to selected period basis, it changes 'calendar' value to 'fiscal' and vice versa.
     * It updates basisLabel
     * @param value selected period basis
     */
    onPeriodBasisChange(value) {
        const isCalYear = getIsCalendarBasisId(value);
        this.basisLabel = getBasisPrefix(isCalYear);
        this.setDataInfoMsg();
        this.absolutePeriodFromBasisOptionList = formatAbsolutePeriodBasisOptionList(this.absolutePeriodFromBasisOptionList, this.periodFromBasisOptionValue, value);
        this.relativePeriodFromBasisOptionList = formatRelativePeriodBasisOptionList(this.relativePeriodFromBasisOptionList, this.periodFromBasisOptionValue, value);
        if (this.absolutePeriodToBasisOptionList) {
            this.absolutePeriodToBasisOptionList = formatAbsolutePeriodBasisOptionList(this.absolutePeriodToBasisOptionList, this.periodToBasisOptionValue, value);
        }
        if (this.relativePeriodToBasisOptionList) {
            this.relativePeriodToBasisOptionList = formatRelativePeriodBasisOptionList(this.relativePeriodToBasisOptionList, this.periodToBasisOptionValue, value);
        }
    }
    /**
     * Determines in interim basis and ltm/ntm/stm is selected or not.
     * This happens in case multi-select period filter, as we don't allow quarter/half year and ntm/stm/ltm combination
     * @param annualCheckboxValue annual checkbox check value
     * @param otherPeriodCheckboxValue other period checkbox value
     * @param fromBasisOptionValue from basis option id
     * @param toBasisOptionValue to basis option id
     * @return isValid determines if the combination is selected or not
     */
    checkIfInterimsAndOtherPeriodAreSelected(annualCheckboxValue, otherPeriodsCheckboxValue, fromBasisOptionValue, toBasisOptionValue) {
        const otherPeriodCheckboxValueExist = otherPeriodsCheckboxValue && otherPeriodsCheckboxValue.length;
        return (this.isMultiSelectPeriodFilter &&
            annualCheckboxValue &&
            otherPeriodCheckboxValueExist &&
            (fromBasisOptionValue !== PERIOD_FULL_YEAR_ID ||
                toBasisOptionValue !== PERIOD_FULL_YEAR_ID));
    }
    /**
     * Executes on change of checkbox
     * Determines if one of the checkbox is selected or not, and set error message if all checkbox are unchecked
     * @param annualCheckboxValue annual checkbox check value
     * @param otherPeriodCheckboxValue other period checkbox value
     * @param fromBasisOption from basis option id
     * @param toBasisOption to basis option id
     * @return isValid determines if one of the checkbox is selected or not
     */
    validateCheckboxAndSetErrorMessage(annualCheckboxValue, otherPeriodCheckboxValue, fromBasisOption, toBasisOption) {
        const isAnnulValueExist = annualCheckboxValue;
        const otherPeriodCheckboxValueExist = otherPeriodCheckboxValue && otherPeriodCheckboxValue.length;
        if (!isAnnulValueExist && !otherPeriodCheckboxValueExist) {
            this.errorMessage = ERROR_MESSAGE.requiredPeriodOrOtherPeriod;
            this.emitErrorMessage(this.errorMessage);
            return false;
        }
        const isPeriodBasisNotSimilar = isPeriodFromToBasisDifferent(fromBasisOption, toBasisOption);
        if (annualCheckboxValue && isPeriodBasisNotSimilar) {
            this.errorMessage = ERROR_MESSAGE.similarBasis;
            this.emitErrorMessage(this.errorMessage);
            return false;
        }
        if (this.checkIfInterimsAndOtherPeriodAreSelected(annualCheckboxValue, otherPeriodCheckboxValue, this.periodFromBasisOptionValue, this.periodToBasisOptionValue)) {
            this.errorMessage = ERROR_MESSAGE.interimAndOther;
            this.emitErrorMessage(this.errorMessage);
            return false;
        }
        this.errorMessage = "";
        this.emitErrorMessage(this.errorMessage);
        return true;
    }
    /**
     * Executes on annual period checkbox change
     * Annual/Interim period checkbox appears in case of multi-period filter
     * It checks the validation of checkboxes
     */
    handleAnnualPeriodCheckboxChange(value) {
        this.validateCheckboxAndSetErrorMessage(value, this.periodOtherValue, this.periodFromBasisOptionValue, this.periodToBasisOptionValue);
    }
    /**
     * Executes on annual period radio change
     * Annual/Interim period radio appears in case of single-period filter
     * It removes the other period radio values, if annual/interim radio is selected
     */
    handleAnnualPeriodRadioChange() {
        this.otherPeriodRadioValue = undefined;
    }
    /**
     * Executes on other_period(NTM,STM,LTM) checkbox value change
     * It assigns selected values to periodOtherValue
     * It checks the validation of checkboxes
     * @param value selected other periods
     */
    handleOtherItemValueChange(value) {
        this.periodOtherValue = value;
        this.validateCheckboxAndSetErrorMessage(this.isAnnualSelected, value, this.periodFromBasisOptionValue, this.periodToBasisOptionValue);
    }
    /**
     * Executes on other_period(NTM,STM,LTM) radio value change
     * It assigns selected values to periodOtherValue
     * It removes the annual/interim radio value, if ntm/ltm/stm selected
     * @param value selected other periods
     */
    handleOtherItemRadioValueChange(value) {
        this.otherPeriodRadioValue = value;
        if (this.isAnnualSelected) {
            this.isAnnualSelected = false;
        }
    }
    /**
     * It reset the period from-to basis option to default value i.e Y(yearly)
     * It updates from-to basis option list and makes the yearly basis option true
     */
    resetPeriodFromToBasisToYearly() {
        this.periodFromBasisOptionValue = PERIOD_FULL_YEAR_ID;
        this.periodToBasisOptionValue = PERIOD_FULL_YEAR_ID;
        if (this.absolutePeriodFromBasisOptionList) {
            this.absolutePeriodFromBasisOptionList = this.absolutePeriodFromBasisOptionList.map((el) => ({ ...el, selected: el.name === PERIOD_FULL_YEAR_ID }));
        }
        if (this.relativePeriodFromBasisOptionList) {
            this.relativePeriodFromBasisOptionList = this.relativePeriodFromBasisOptionList.map((el) => ({ ...el, selected: el.name === PERIOD_FULL_YEAR_ID }));
        }
        if (this.absolutePeriodToBasisOptionList) {
            this.absolutePeriodToBasisOptionList = this.absolutePeriodToBasisOptionList.map((el) => ({ ...el, selected: el.name === PERIOD_FULL_YEAR_ID }));
        }
        if (this.relativePeriodToBasisOptionList) {
            this.relativePeriodToBasisOptionList = this.relativePeriodToBasisOptionList.map((el) => ({ ...el, selected: el.name === PERIOD_FULL_YEAR_ID }));
        }
    }
    /**
     * This function can be called by parent component to read the current state of this component.
     * This is useful when we don't want to show this component's apply button and instead want to control
     * it from outside.
     * e.g. Lets say there is one mega filter and it has more than one filters of different kinds. And all
     * these filters do not have individual apply button. There is only one common apply button. In this case
     * the parent component which has apply button can read the current state of this component on click of
     * apply or on any other event which is not related to this component.
     * @returns current state {periodData: PeriodState, periodUserSettingData: PeriodUserSettingObj}
     */
    getCurrentState() {
        this.hasClickedApply = true;
        if (this.errorMessage) {
            return;
        }
        if (this.isMultiSelectPeriodFilter) {
            const isPeriodFromToBasisOfDifferentType = isPeriodFromToBasisDifferent(this.periodFromBasisOptionValue, this.periodToBasisOptionValue);
            // reset period basis option value if multi-select period and from-to basis are different
            if (isPeriodFromToBasisOfDifferentType) {
                this.resetPeriodFromToBasisToYearly();
            }
        }
        const updatedPeriodData = {
            type: this.periodData.type.map((item) => ({
                ...item,
                selected: item.name === this.periodTypeValue
            })),
            basis: this.periodData.basis.map((item) => ({
                ...item,
                selected: item.name === this.periodBasisValue
            })),
            from_to: {
                selected_from: this.periodFrom,
                selected_to: this.periodTo
            },
            other_period: this.periodData.other_period.map((item) => ({
                ...item,
                selected: this.isMultiSelectPeriodFilter
                    ? this.periodOtherValue.includes(item.name)
                    : item.name === this.otherPeriodRadioValue
            })),
            period_basis: {
                abs: {
                    from_basis: this.absolutePeriodFromBasisOptionList &&
                        this.absolutePeriodFromBasisOptionList.length
                        ? this.absolutePeriodFromBasisOptionList
                        : undefined,
                    to_basis: this.absolutePeriodToBasisOptionList &&
                        this.absolutePeriodToBasisOptionList.length
                        ? this.absolutePeriodToBasisOptionList
                        : undefined
                },
                rel: {
                    from_basis: this.relativePeriodFromBasisOptionList &&
                        this.relativePeriodFromBasisOptionList.length
                        ? this.relativePeriodFromBasisOptionList
                        : undefined,
                    to_basis: this.relativePeriodToBasisOptionList &&
                        this.relativePeriodToBasisOptionList.length
                        ? this.relativePeriodToBasisOptionList
                        : undefined
                }
            },
            is_annual_selected: this.isAnnualSelected
        };
        const isAbsType = getIsAbsolutePeriodTypeId(this.periodTypeValue);
        let periodFrom = this.periodFrom;
        let periodFromBasis = this.periodFromBasisOptionValue;
        const periodToExist = periodYearValueExist(this.periodTo);
        let periodTo = periodToExist ? this.periodTo : undefined;
        let periodToBasis = periodToExist
            ? this.periodToBasisOptionValue
            : undefined;
        if (isAbsType) {
            const fromBasisData = convertAbsToRel(periodFrom, this.periodFromBasisOptionValue);
            periodFrom = fromBasisData.year;
            periodFromBasis = fromBasisData.basis;
            if (periodTo) {
                const toBasisData = convertAbsToRel(periodTo, this.periodToBasisOptionValue);
                periodTo = toBasisData.year;
                periodToBasis = toBasisData.basis;
            }
        }
        const periodUserSettingData = {
            type: this.periodTypeValue,
            basis: this.periodBasisValue,
            from: periodFrom,
            to: periodTo,
            other: this.isMultiSelectPeriodFilter
                ? this.periodOtherValue
                : this.otherPeriodRadioValue
                    ? [this.otherPeriodRadioValue]
                    : [],
            period_type_basis_from: periodFromBasis || PERIOD_FULL_YEAR_ID,
            period_type_basis_to: periodToBasis,
            is_annual_selected: this.isAnnualSelected
        };
        return {
            updatedPeriodData,
            periodUserSettingData
        };
    }
    /**
     * Executes on click of apply button
     * It updates the periodData state according to selected filter
     * Emits `onApply` with updated periodData  state and selected userSetting object
     * Hides the popup
     */
    handleApply() {
        let data = this.getCurrentState();
        this.onApply.emit(data);
    }
    emitErrorMessage(errorMessage) {
        this.onError.emit(errorMessage);
    }
    /**
     * Validates periodTo year value on value change
     * @param value: periodTo value - it is a number, but when user empties the field, then the value is empty string
     */
    handlePeriodToValueChange(value) {
        //if periodTo value isn't number or doesn't exist
        if (!periodYearValueExist(value)) {
            this.isPeriodToYearInvalid = true;
        }
        else {
            //if periodTo value is greater than max year value
            const isPeriodToGTMax = value > this.maxValue;
            if (isPeriodToGTMax) {
                this.periodTo = this.maxValue;
            }
            //if periodTo value is less than min year value
            const isPeriodToLTMin = value < this.minValue;
            if (isPeriodToLTMin) {
                this.periodTo = this.minValue;
            }
            //if periodTo value is less than periodFrom year value
            if (this.isMultiSelectPeriodFilter &&
                typeof this.periodFrom === "number") {
                const isPeriodToLTPeriodFrom = value < this.periodFrom;
                if (isPeriodToLTPeriodFrom) {
                    this.periodTo = this.periodFrom;
                }
            }
            this.isPeriodToYearInvalid = false;
        }
    }
    /**
     * Validates periodFrom year value on value change
     * @param value: periodTo value
     */
    handlePeriodFromValueChange(value) {
        //if periodFrom value isn't number or doesn't exist
        if (!periodYearValueExist(value)) {
            this.isPeriodFromYearInvalid = true;
        }
        else {
            //if periodFrom value is less than min year value
            const isPeriodFromLTMin = value < this.minValue;
            if (isPeriodFromLTMin) {
                this.periodFrom = this.minValue;
            }
            //if periodFrom value is greater than max year value
            const isPeriodFromGTMax = value > this.maxValue;
            if (isPeriodFromGTMax) {
                this.periodFrom = this.maxValue;
            }
            //if periodFrom value is greater than periodTo year value
            if (this.isMultiSelectPeriodFilter && typeof this.periodTo === "number") {
                const isPeriodFromGTPeriodTo = value > this.periodTo;
                if (isPeriodFromGTPeriodTo) {
                    this.periodFrom = this.periodTo;
                }
            }
            this.isPeriodFromYearInvalid = false;
        }
    }
    /**
     * Calculates the min max range of year, according to period type and basis
     * @param isAbsType: periodFrom basis value
     * @param basisOption: periodTo basis value
     */
    updateMinMaxRange(isAbsType, basisOption) {
        const { min, max } = getMinMAxYearRange(isAbsType, basisOption, this.periodMinMax);
        this.minValue = min;
        this.maxValue = max;
    }
    /**
     * Set period basis error message if the selected basis isn't of same type.
     * Both from-to basis should belong to same categories i.e. Quarterly, half yearly and yearly
     * @param fromBasis: periodFrom basis value
     * @param toBasis: periodTo basis value
     */
    checkBasisValidAndSetErrorMessage(fromBasis, toBasis) {
        const isPeriodBasisNotSimilar = isPeriodFromToBasisDifferent(fromBasis, toBasis);
        if (isPeriodBasisNotSimilar) {
            this.errorMessage = ERROR_MESSAGE.similarBasis;
            this.emitErrorMessage(this.errorMessage);
            return false;
        }
        this.errorMessage = "";
        this.emitErrorMessage(this.errorMessage);
        return true;
    }
    /**
     * Set period basis error message if interims and rolling periods are selected.
     * In multi-range period selector interims and other-period combination is not allowed
     * @param fromBasis: periodFrom basis value
     * @param toBasis: periodTo basis value
     * @param isAnnualSelected: annual period checkbox/radio selected or not
     * @param periodOtherValue: selected other period value array
     * @return isValid: determines if valid or not
     */
    checkInterimsAndRollingPeriodSelectedAndSetErr(fromBasis, toBasis, isAnnualSelected, periodOtherValue) {
        if (this.checkIfInterimsAndOtherPeriodAreSelected(isAnnualSelected, periodOtherValue, fromBasis, toBasis)) {
            this.errorMessage = ERROR_MESSAGE.interimAndOther;
            this.emitErrorMessage(this.errorMessage);
            return false;
        }
        this.errorMessage = "";
        this.emitErrorMessage(this.errorMessage);
        return true;
    }
    /**
     * Check if period from-to value is within min-max range
     * If it is invalid then update it
     * User can try various(h/q/y) combinations which leads to incorrect from-to value, so we manually update the from-to value
     * @param fromValue: periodFrom value
     * @param toValue: periodTo value
     */
    checkAndUpdatePeriodFromToValue(fromValue, toValue) {
        if (typeof fromValue === "number") {
            if (fromValue < this.minValue) {
                this.periodFrom = this.minValue;
            }
        }
        if (typeof toValue === "number") {
            if (toValue > this.maxValue) {
                this.periodTo = this.maxValue;
            }
        }
    }
    /**
     * Validates periodFrom basis value on selecting basis
     * @param item: selected period basis value
     */
    handlePeriodFrmBasisChange(item) {
        if (item === undefined || item === null) {
            this.errorMessage = ERROR_MESSAGE.requiredBasis;
            this.emitErrorMessage(this.errorMessage);
        }
        else {
            const isAbsType = getIsAbsolutePeriodTypeId(this.periodTypeValue);
            if (this.isMultiSelectPeriodFilter && this.periodToBasisOptionValue) {
                const isFromToBasisValid = this.checkBasisValidAndSetErrorMessage(item, this.periodToBasisOptionValue);
                if (isFromToBasisValid) {
                    this.updateMinMaxRange(isAbsType, item);
                    this.checkInterimsAndRollingPeriodSelectedAndSetErr(item, this.periodToBasisOptionValue, this.isAnnualSelected, this.periodOtherValue);
                }
            }
            else {
                this.errorMessage = "";
                this.emitErrorMessage(this.errorMessage);
                this.updateMinMaxRange(isAbsType, item);
            }
            // manually update incorrect from-to value if any, after resolving validation errors 
            this.checkAndUpdatePeriodFromToValue(this.periodFrom, this.periodTo);
        }
        if (this.absolutePeriodFromBasisOptionList) {
            this.absolutePeriodFromBasisOptionList = this.absolutePeriodFromBasisOptionList.map((el) => ({ ...el, selected: el.name === item }));
        }
        if (this.relativePeriodFromBasisOptionList) {
            this.relativePeriodFromBasisOptionList = this.relativePeriodFromBasisOptionList.map((el) => ({ ...el, selected: el.name === item }));
        }
    }
    /**
     * Validates periodTo basis value on selecting basis
     * @param item: selected period basis value
     */
    handlePeriodToBasisChange(item) {
        if (item === undefined || item === null) {
            this.errorMessage = ERROR_MESSAGE.requiredBasis;
            this.emitErrorMessage(this.errorMessage);
        }
        else {
            const isAbsType = getIsAbsolutePeriodTypeId(this.periodTypeValue);
            if (this.isMultiSelectPeriodFilter && this.periodFromBasisOptionValue) {
                const isFromToBasisValid = this.checkBasisValidAndSetErrorMessage(this.periodFromBasisOptionValue, item);
                if (isFromToBasisValid) {
                    this.updateMinMaxRange(isAbsType, item);
                }
                this.checkInterimsAndRollingPeriodSelectedAndSetErr(this.periodFromBasisOptionValue, item, this.isAnnualSelected, this.periodOtherValue);
            }
            else {
                this.errorMessage = "";
                this.emitErrorMessage(this.errorMessage);
                this.updateMinMaxRange(isAbsType, item);
            }
            // manually update incorrect from-to value if any, after resolving validation errors 
            this.checkAndUpdatePeriodFromToValue(this.periodFrom, this.periodTo);
        }
        if (this.absolutePeriodToBasisOptionList) {
            this.absolutePeriodToBasisOptionList = this.absolutePeriodToBasisOptionList.map((el) => ({ ...el, selected: el.name === item }));
        }
        if (this.relativePeriodToBasisOptionList) {
            this.relativePeriodToBasisOptionList = this.relativePeriodToBasisOptionList.map((el) => ({ ...el, selected: el.name === item }));
        }
    }
}
PeriodFilterContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterContentComponent, deps: [{ token: i1.VAIconRegistryService }], target: i0.ɵɵFactoryTarget.Component });
PeriodFilterContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: PeriodFilterContentComponent, selector: "va-period-filter-content", inputs: { periodData: "periodData", periodMinMax: "periodMinMax", hidePeriodType: "hidePeriodType", hidePeriodBasis: "hidePeriodBasis", showApplyBtn: "showApplyBtn", dataAvailable: "dataAvailable" }, outputs: { onApply: "onApply", onError: "onError" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"period-filter-content\">\n  <div *ngIf=\"hasOtherPeriodData\" class=\"mb-12\">\n    <div\n      nz-checkbox\n      [(ngModel)]=\"isAnnualSelected\"\n      *ngIf=\"isMultiSelectPeriodFilter\"\n      (ngModelChange)=\"handleAnnualPeriodCheckboxChange($event)\"\n      class=\"period-filter-content-label\"\n    >\n      {{ annualLabel }}\n    </div>\n    <label\n      nz-radio\n      [(ngModel)]=\"isAnnualSelected\"\n      *ngIf=\"!isMultiSelectPeriodFilter\"\n      (ngModelChange)=\"handleAnnualPeriodRadioChange()\"\n      class=\"period-filter-content-label\"\n      >{{ annualLabel }}</label\n    >\n  </div>\n  <div class=\"period-filter-content-label  mb-12\" *ngIf=\"!hasOtherPeriodData\">\n    {{ annualLabel }}\n  </div>\n  <div\n    class=\"period-annual-wrapper\"\n    [ngClass]=\"{ 'period-annual-wrapper--disabled': !isAnnualSelected }\"\n  >\n    <div *ngIf=\"!hidePeriodType\" class=\"period-filter-radio-container mb-16\">\n      <div class=\"period-filter-content-label period-filter-radio-box\">\n        Periods:\n      </div>\n      <div class=\"ml-12\">\n        <nz-radio-group\n          [(ngModel)]=\"periodTypeValue\"\n          (ngModelChange)=\"onPeriodTypeChange($event)\"\n          *ngIf=\"periodData\"\n        >\n          <label\n            *ngFor=\"let item of periodData.type\"\n            nz-radio\n            [nzValue]=\"item.name\"\n            class=\"mr-40\"\n            >{{ item.label }}</label\n          >\n        </nz-radio-group>\n      </div>\n    </div>\n    <div  *ngIf=\"!hidePeriodBasis\" class=\"period-filter-radio-container mb-16\">\n      <div class=\"period-filter-content-label  period-filter-radio-box\">\n        Basis:\n      </div>\n      <div class=\"ml-12\">\n        <nz-radio-group\n          [(ngModel)]=\"periodBasisValue\"\n          (ngModelChange)=\"onPeriodBasisChange($event)\"\n          *ngIf=\"periodData\"\n        >\n          <label\n            *ngFor=\"let item of periodData.basis\"\n            nz-radio\n            [nzValue]=\"item.name\"\n            class=\"mr-40\"\n            >{{ item.label }}</label\n          >\n        </nz-radio-group>\n      </div>\n    </div>\n    <div class=\"period-filter-from-to-wrapper mb-4\">\n      <!-- left one -->\n      <div class=\"period-filter-from-to-item\">\n        <div class=\"period-filter-content-label\">\n          {{ isMultiSelectPeriodFilter ? \"From\" : \"Select\" }}:\n        </div>\n        <div class=\"period-filter-from-to-block-wrapper\">\n          <div class=\"period-filter-from-to-block\">\n            <div class=\"period-filter-from-to-label\">\n              Basis\n            </div>\n            <div>\n              <nz-select\n                *ngIf=\"!hasOnlyYearBasis\"\n                [(ngModel)]=\"periodFromBasisOptionValue\"\n                class=\"period-filter-basis-select\"\n                nzDropdownClassName=\"period-filter-basis-dropdown\"\n                (ngModelChange)=\"handlePeriodFrmBasisChange($event)\"\n              >\n                <nz-option\n                  nzCustomContent\n                  *ngFor=\"\n                    let item of periodTypeValue === 'rel'\n                      ? relativePeriodFromBasisOptionList\n                      : absolutePeriodFromBasisOptionList\n                  \"\n                  [nzValue]=\"item.name\"\n                  [nzLabel]=\"item.formattedLabel\"\n                >\n                  <va-icon *ngIf=\"periodFromBasisOptionValue === item.name\" size=\"small\" name=\"check\"></va-icon>\n                  {{item.formattedLabel}}\n                </nz-option>\n              </nz-select>\n              <div class=\"period-filter-value-label\" *ngIf=\"hasOnlyYearBasis\">\n                {{ basisLabel }}\n              </div>\n            </div>\n          </div>\n          <div\n            class=\"period-filter-from-to-block period-filter-from-to-block-right\"\n          >\n            <div class=\"period-filter-from-to-label\">Year</div>\n            <div>\n              <nz-input-number\n                [nzMin]=\"minValue\"\n                [nzMax]=\"\n                  periodTo === '' || periodTo === undefined || periodTo === null\n                    ? maxValue\n                    : periodTo\n                \"\n                [(ngModel)]=\"periodFrom\"\n                [nzPrecision]=\"0\"\n                nzPlaceHolder=\"Select\"\n                class=\"val-period-year-selector\"\n                [ngClass]=\"{ 'period-year--error': isPeriodFromYearInvalid }\"\n                (ngModelChange)=\"handlePeriodFromValueChange($event)\"\n              ></nz-input-number>\n            </div>\n          </div>\n        </div>\n      </div>\n\n      <!-- right one -->\n      <div class=\"period-filter-from-to-item\" *ngIf=\"isMultiSelectPeriodFilter\">\n        <div class=\"period-filter-content-label\">\n          To:\n        </div>\n        <div class=\"period-filter-from-to-block-wrapper\">\n          <div class=\"period-filter-from-to-block\">\n            <div class=\"period-filter-from-to-label\">\n              Basis\n            </div>\n            <div class=\"period-filter-from-to-basis-value\">\n              <nz-select\n                *ngIf=\"!hasOnlyYearBasis\"\n                [(ngModel)]=\"periodToBasisOptionValue\"\n                class=\"period-filter-basis-select\"\n                nzDropdownClassName=\"period-filter-basis-dropdown\"\n                (ngModelChange)=\"handlePeriodToBasisChange($event)\"\n              >\n                <nz-option\n                  nzCustomContent\n                  *ngFor=\"\n                    let item of periodTypeValue === 'rel'\n                      ? relativePeriodToBasisOptionList\n                      : absolutePeriodToBasisOptionList\n                  \"\n                  [nzValue]=\"item.name\"\n                  [nzLabel]=\"item.formattedLabel\"\n                >\n                  <va-icon *ngIf=\"periodToBasisOptionValue === item.name\" size=\"small\" name=\"check\"></va-icon>\n                  {{item.formattedLabel}}\n                </nz-option>\n              </nz-select>\n              <div class=\" period-filter-value-label\" *ngIf=\"hasOnlyYearBasis\">\n                {{ basisLabel }}\n              </div>\n            </div>\n          </div>\n          <div\n            class=\"period-filter-from-to-block period-filter-from-to-block-right\"\n          >\n            <div class=\"period-filter-from-to-label\">Year</div>\n            <div>\n              <nz-input-number\n                [nzMin]=\"\n                  periodFrom === '' || periodFrom === undefined || periodFrom === null\n                    ? minValue\n                    : periodFrom\n                \"\n                [nzMax]=\"maxValue\"\n                [(ngModel)]=\"periodTo\"\n                [nzPrecision]=\"0\"\n                class=\"val-period-year-selector\"\n                [ngClass]=\"{ 'period-year--error': isPeriodToYearInvalid }\"\n                (ngModelChange)=\"handlePeriodToValueChange($event)\"\n              ></nz-input-number>\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div\n      class=\"period-filter-info-holder mb-16\"\n      *ngIf=\"dataInfo && dataInfo.to && dataInfo.from\"\n    >\n      Available: {{basisLabel}} {{dataInfo.from}} to {{basisLabel}} {{dataInfo.to}}\n    </div>\n    <div class=\"period-filter-e-msg-holder mb-16\" *ngIf=\"errorMessage\">\n      <va-icon\n        size=\"small\"\n        name=\"exclamation-triangle\"\n        class=\"period-filter-e-info-icon\"\n      ></va-icon>\n      <div class=\"period-filter-error-msg\">&nbsp;{{ errorMessage }}</div>\n    </div>\n  </div>\n  <div\n    class=\"other-period-container\"\n    *ngIf=\"\n      periodData && periodData.other_period && periodData.other_period.length\n    \"\n  >\n    <nz-checkbox-wrapper\n      (nzOnChange)=\"handleOtherItemValueChange($event)\"\n      *ngIf=\"isMultiSelectPeriodFilter\"\n    >\n      <div\n        *ngFor=\"let item of periodData.other_period\"\n        class=\"val-period-checkbox\"\n      >\n        <label nz-checkbox [nzValue]=\"item.name\" [ngModel]=\"item.selected\">{{\n          item.label\n        }}</label>\n        <div\n          *ngIf=\"item.name === 'stm'\"\n          class=\"ca-tooltip-info-wrapper\"\n          nz-tooltip\n          nzTooltipPlacement=\"bottom\"\n          [nzTooltipTitle]=\"stmInfoTemplate\"\n        >\n          <va-icon size=\"small\" name=\"info-circle\"></va-icon>\n        </div>\n      </div>\n    </nz-checkbox-wrapper>\n    <nz-radio-group\n      [(ngModel)]=\"otherPeriodRadioValue\"\n      (ngModelChange)=\"handleOtherItemRadioValueChange($event)\"\n      *ngIf=\"!isMultiSelectPeriodFilter\"\n    >\n      <div\n        *ngFor=\"let item of periodData.other_period\"\n        class=\"val-period-checkbox\"\n      >\n        <label nz-radio [nzValue]=\"item.name\">{{ item.label }}</label>\n        <div\n          *ngIf=\"item.name === 'stm'\"\n          class=\"ca-tooltip-info-wrapper\"\n          nz-tooltip\n          nzTooltipPlacement=\"bottom\"\n          [nzTooltipTitle]=\"stmInfoTemplate\"\n        >\n          <va-icon size=\"small\" name=\"info-circle\"></va-icon>\n        </div>\n      </div>\n    </nz-radio-group>\n  </div>\n\n  <div class=\"period-filter-footer\">\n    <va-button\n      *ngIf=\"showApplyBtn\"\n      class=\"period-filter-button\"\n      btnType=\"solid\"\n      size=\"medium\"\n      [isDisabled]=\"\n        errorMessage || isPeriodToYearInvalid || isPeriodFromYearInvalid\n      \"\n      (onClick)=\"handleApply()\"\n    >\n      Apply\n    </va-button>\n  </div>\n</div>\n\n<ng-template #stmInfoTemplate>\n  <div class=\"ca-tooltip-content\">\n    <span> <va-icon size=\"small\" name=\"info-circle\"></va-icon></span>\n    &nbsp;\"Second Twelve Months (STM) uses the estimates for the 12 months\n    following the period in NTM.\"\n  </div>\n</ng-template>\n", styles: [".period-filter-radio-container{display:flex}.period-filter-radio-box{width:70px}.period-filter-content{padding:16px 20px}.period-filter-content-label{color:#222;font-size:14px;font-weight:600}.period-filter-footer{border-top:1px solid #d8d8d8;padding-top:12px;display:flex;flex-direction:column}.mb-16{margin-bottom:16px}.mb-12{margin-bottom:12px}.mr-40{margin-right:40px}.mb-8{margin-bottom:8px}.mb-4{margin-bottom:4px}::ng-deep .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner{border-color:#36abad}::ng-deep .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner:after{background-color:#36abad}::ng-deep .ant-radio:hover .ant-radio-inner{border-color:#36abad}::ng-deep .ant-checkbox-checked .ant-checkbox-inner{background-color:#36abad;border-color:#36abad}::ng-deep .ant-checkbox:hover .ant-checkbox-inner{border-color:#36abad}.ant-btn-primary{background-color:#36abad;border-color:#36abad}::ng-deep .period-filter-overlay{min-width:400px}::ng-deep .period-filter-overlay .ant-popover-inner-content{padding:0}::ng-deep .val-period-year-selector{width:80px}::ng-deep .val-period-year-selector.period-year--error{border-color:#e61f00}::ng-deep .val-period-year-selector.period-year--error:focus,::ng-deep .val-period-year-selector.period-year--error:hover{border-color:#e61f00}::ng-deep .val-period-year-selector .ant-input-number-handler-wrap{opacity:1}::ng-deep .val-period-year-selector:focus,::ng-deep .val-period-year-selector:hover{border-color:#36abad;box-shadow:none}::ng-deep .val-period-year-selector .ant-input-number-handler-down-inner,::ng-deep .val-period-year-selector .ant-input-number-handler-up-inner{color:#454545}::ng-deep .val-period-year-selector .ant-input-number-handler-down-inner:hover,::ng-deep .val-period-year-selector .ant-input-number-handler-up-inner:hover{color:#36abad}::ng-deep .val-period-year-selector .ant-input-number-handler:hover{color:#36abad}.val-period-checkbox{display:inline-flex;margin-right:16px}.period-filter-from-to-item{width:48%}.period-filter-from-to-wrapper{display:flex;justify-content:space-between}.period-filter-from-to-label{color:#979797;font-size:14px;margin-bottom:2px}.period-filter-from-to-block-wrapper{display:flex}.period-filter-from-to-block{display:inline-block}.period-filter-from-to-block:not(:last-child){margin-right:12px}.period-filter-from-to-block ::ng-deep .ant-select .ant-select-selector{padding:0 8px}.period-filter-from-to-block ::ng-deep .ant-select .ant-select-selector .ant-select-selection-item{padding-right:8px}.period-filter-from-to-block-right{flex:1}.period-filter-value-label{color:#222;font-size:14px;text-align:center;min-height:32px;align-items:center;justify-content:center;display:flex}::ng-deep .period-filter-basis-select{width:68px}::ng-deep .period-filter-basis-select .ant-select-selection:focus,::ng-deep .period-filter-basis-select .ant-select-selection:hover{border-color:#36abad;box-shadow:none}::ng-deep .period-filter-basis-select.ant-select-open .ant-select-arrow-icon{color:#36abad}::ng-deep .period-filter-basis-select .ant-select-arrow{right:8px}::ng-deep .period-filter-basis-select .ant-select-selection--single .ant-select-selection__rendered{margin-right:16px;margin-left:8px}::ng-deep .period-filter-basis-dropdown{min-width:98px}::ng-deep .period-filter-basis-dropdown .ant-select-dropdown::-webkit-scrollbar,::ng-deep .period-filter-basis-dropdown .cdk-virtual-scroll-viewport::-webkit-scrollbar{width:6px!important;height:6px!important}::ng-deep .period-filter-basis-dropdown .ant-select-dropdown::-webkit-scrollbar-track,::ng-deep .period-filter-basis-dropdown .cdk-virtual-scroll-viewport::-webkit-scrollbar-track{background:#c5ccd6;border-left:2px solid white;border-right:2px solid white}::ng-deep .period-filter-basis-dropdown .ant-select-dropdown::-webkit-scrollbar-thumb,::ng-deep .period-filter-basis-dropdown .cdk-virtual-scroll-viewport::-webkit-scrollbar-thumb{background-color:#36abad;border:none;border-radius:4px}::ng-deep .period-filter-basis-dropdown .ant-select-item{border-left:3px solid transparent;position:relative;padding-left:24px}::ng-deep .period-filter-basis-dropdown .ant-select-item:hover{border-color:#36abad}::ng-deep .period-filter-basis-dropdown .ant-select-item-option-selected{padding-left:4px}::ng-deep .period-filter-basis-dropdown .ant-select-item-option-selected va-icon{padding-right:2px;color:#36abad;font-size:14px}.period-filter-error-msg{color:#ca0000;font-size:11px}.period-filter-e-info-icon{color:#ca0000;font-size:14px;display:block}.period-filter-e-msg-holder{display:flex;align-items:center}.period-annual-wrapper{margin-left:24px}.period-annual-wrapper.period-annual-wrapper--disabled{opacity:.4;pointer-events:none}.period-filter-button{align-self:flex-end}.other-period-container{border-top:1px solid #d8d8d8;padding-top:12px;padding-bottom:12px}.period-filter-info-holder{color:#667284;font-size:12px;font-weight:400}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3.NzCheckboxComponent, selector: "[nz-checkbox]", inputs: ["nzValue", "nzAutoFocus", "nzDisabled", "nzIndeterminate", "nzChecked", "nzId"], outputs: ["nzCheckedChange"], exportAs: ["nzCheckbox"] }, { kind: "component", type: i3.NzCheckboxWrapperComponent, selector: "nz-checkbox-wrapper", outputs: ["nzOnChange"], exportAs: ["nzCheckboxWrapper"] }, { kind: "component", type: i4.NzRadioComponent, selector: "[nz-radio],[nz-radio-button]", inputs: ["nzValue", "nzDisabled", "nzAutoFocus"], exportAs: ["nzRadio"] }, { kind: "component", type: i4.NzRadioGroupComponent, selector: "nz-radio-group", inputs: ["nzDisabled", "nzButtonStyle", "nzSize", "nzName"], exportAs: ["nzRadioGroup"] }, { kind: "component", type: i5.NzOptionComponent, selector: "nz-option", inputs: ["nzLabel", "nzValue", "nzDisabled", "nzHide", "nzCustomContent"], exportAs: ["nzOption"] }, { kind: "component", type: i5.NzSelectComponent, selector: "nz-select", inputs: ["nzId", "nzSize", "nzStatus", "nzOptionHeightPx", "nzOptionOverflowSize", "nzDropdownClassName", "nzDropdownMatchSelectWidth", "nzDropdownStyle", "nzNotFoundContent", "nzPlaceHolder", "nzPlacement", "nzMaxTagCount", "nzDropdownRender", "nzCustomTemplate", "nzSuffixIcon", "nzClearIcon", "nzRemoveIcon", "nzMenuItemSelectedIcon", "nzTokenSeparators", "nzMaxTagPlaceholder", "nzMaxMultipleCount", "nzMode", "nzFilterOption", "compareWith", "nzAllowClear", "nzBorderless", "nzShowSearch", "nzLoading", "nzAutoFocus", "nzAutoClearSearchValue", "nzServerSearch", "nzDisabled", "nzOpen", "nzSelectOnTab", "nzBackdrop", "nzOptions", "nzShowArrow"], outputs: ["nzOnSearch", "nzScrollToBottom", "nzOpenChange", "nzBlur", "nzFocus"], exportAs: ["nzSelect"] }, { kind: "directive", type: i6.NzTooltipDirective, selector: "[nz-tooltip]", inputs: ["nzTooltipTitle", "nzTooltipTitleContext", "nz-tooltip", "nzTooltipTrigger", "nzTooltipPlacement", "nzTooltipOrigin", "nzTooltipVisible", "nzTooltipMouseEnterDelay", "nzTooltipMouseLeaveDelay", "nzTooltipOverlayClassName", "nzTooltipOverlayStyle", "nzTooltipArrowPointAtCenter", "nzTooltipColor"], outputs: ["nzTooltipVisibleChange"], exportAs: ["nzTooltip"] }, { kind: "directive", type: i7.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i7.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i8.NzInputNumberComponent, selector: "nz-input-number", inputs: ["nzSize", "nzMin", "nzMax", "nzParser", "nzPrecision", "nzPrecisionMode", "nzPlaceHolder", "nzStatus", "nzStep", "nzInputMode", "nzId", "nzDisabled", "nzReadOnly", "nzAutoFocus", "nzBorderless", "nzFormatter"], outputs: ["nzBlur", "nzFocus"], exportAs: ["nzInputNumber"] }, { kind: "component", type: i1.IconComponent, selector: "va-icon", inputs: ["spin", "isFontAwesome", "styles", "size", "name"] }, { kind: "component", type: i9.ButtonComponent, selector: "va-button", inputs: ["btnType", "state", "size", "isDisabled", "isOnlyIcon"], outputs: ["onClick"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterContentComponent, decorators: [{
            type: Component,
            args: [{ selector: "va-period-filter-content", template: "<div class=\"period-filter-content\">\n  <div *ngIf=\"hasOtherPeriodData\" class=\"mb-12\">\n    <div\n      nz-checkbox\n      [(ngModel)]=\"isAnnualSelected\"\n      *ngIf=\"isMultiSelectPeriodFilter\"\n      (ngModelChange)=\"handleAnnualPeriodCheckboxChange($event)\"\n      class=\"period-filter-content-label\"\n    >\n      {{ annualLabel }}\n    </div>\n    <label\n      nz-radio\n      [(ngModel)]=\"isAnnualSelected\"\n      *ngIf=\"!isMultiSelectPeriodFilter\"\n      (ngModelChange)=\"handleAnnualPeriodRadioChange()\"\n      class=\"period-filter-content-label\"\n      >{{ annualLabel }}</label\n    >\n  </div>\n  <div class=\"period-filter-content-label  mb-12\" *ngIf=\"!hasOtherPeriodData\">\n    {{ annualLabel }}\n  </div>\n  <div\n    class=\"period-annual-wrapper\"\n    [ngClass]=\"{ 'period-annual-wrapper--disabled': !isAnnualSelected }\"\n  >\n    <div *ngIf=\"!hidePeriodType\" class=\"period-filter-radio-container mb-16\">\n      <div class=\"period-filter-content-label period-filter-radio-box\">\n        Periods:\n      </div>\n      <div class=\"ml-12\">\n        <nz-radio-group\n          [(ngModel)]=\"periodTypeValue\"\n          (ngModelChange)=\"onPeriodTypeChange($event)\"\n          *ngIf=\"periodData\"\n        >\n          <label\n            *ngFor=\"let item of periodData.type\"\n            nz-radio\n            [nzValue]=\"item.name\"\n            class=\"mr-40\"\n            >{{ item.label }}</label\n          >\n        </nz-radio-group>\n      </div>\n    </div>\n    <div  *ngIf=\"!hidePeriodBasis\" class=\"period-filter-radio-container mb-16\">\n      <div class=\"period-filter-content-label  period-filter-radio-box\">\n        Basis:\n      </div>\n      <div class=\"ml-12\">\n        <nz-radio-group\n          [(ngModel)]=\"periodBasisValue\"\n          (ngModelChange)=\"onPeriodBasisChange($event)\"\n          *ngIf=\"periodData\"\n        >\n          <label\n            *ngFor=\"let item of periodData.basis\"\n            nz-radio\n            [nzValue]=\"item.name\"\n            class=\"mr-40\"\n            >{{ item.label }}</label\n          >\n        </nz-radio-group>\n      </div>\n    </div>\n    <div class=\"period-filter-from-to-wrapper mb-4\">\n      <!-- left one -->\n      <div class=\"period-filter-from-to-item\">\n        <div class=\"period-filter-content-label\">\n          {{ isMultiSelectPeriodFilter ? \"From\" : \"Select\" }}:\n        </div>\n        <div class=\"period-filter-from-to-block-wrapper\">\n          <div class=\"period-filter-from-to-block\">\n            <div class=\"period-filter-from-to-label\">\n              Basis\n            </div>\n            <div>\n              <nz-select\n                *ngIf=\"!hasOnlyYearBasis\"\n                [(ngModel)]=\"periodFromBasisOptionValue\"\n                class=\"period-filter-basis-select\"\n                nzDropdownClassName=\"period-filter-basis-dropdown\"\n                (ngModelChange)=\"handlePeriodFrmBasisChange($event)\"\n              >\n                <nz-option\n                  nzCustomContent\n                  *ngFor=\"\n                    let item of periodTypeValue === 'rel'\n                      ? relativePeriodFromBasisOptionList\n                      : absolutePeriodFromBasisOptionList\n                  \"\n                  [nzValue]=\"item.name\"\n                  [nzLabel]=\"item.formattedLabel\"\n                >\n                  <va-icon *ngIf=\"periodFromBasisOptionValue === item.name\" size=\"small\" name=\"check\"></va-icon>\n                  {{item.formattedLabel}}\n                </nz-option>\n              </nz-select>\n              <div class=\"period-filter-value-label\" *ngIf=\"hasOnlyYearBasis\">\n                {{ basisLabel }}\n              </div>\n            </div>\n          </div>\n          <div\n            class=\"period-filter-from-to-block period-filter-from-to-block-right\"\n          >\n            <div class=\"period-filter-from-to-label\">Year</div>\n            <div>\n              <nz-input-number\n                [nzMin]=\"minValue\"\n                [nzMax]=\"\n                  periodTo === '' || periodTo === undefined || periodTo === null\n                    ? maxValue\n                    : periodTo\n                \"\n                [(ngModel)]=\"periodFrom\"\n                [nzPrecision]=\"0\"\n                nzPlaceHolder=\"Select\"\n                class=\"val-period-year-selector\"\n                [ngClass]=\"{ 'period-year--error': isPeriodFromYearInvalid }\"\n                (ngModelChange)=\"handlePeriodFromValueChange($event)\"\n              ></nz-input-number>\n            </div>\n          </div>\n        </div>\n      </div>\n\n      <!-- right one -->\n      <div class=\"period-filter-from-to-item\" *ngIf=\"isMultiSelectPeriodFilter\">\n        <div class=\"period-filter-content-label\">\n          To:\n        </div>\n        <div class=\"period-filter-from-to-block-wrapper\">\n          <div class=\"period-filter-from-to-block\">\n            <div class=\"period-filter-from-to-label\">\n              Basis\n            </div>\n            <div class=\"period-filter-from-to-basis-value\">\n              <nz-select\n                *ngIf=\"!hasOnlyYearBasis\"\n                [(ngModel)]=\"periodToBasisOptionValue\"\n                class=\"period-filter-basis-select\"\n                nzDropdownClassName=\"period-filter-basis-dropdown\"\n                (ngModelChange)=\"handlePeriodToBasisChange($event)\"\n              >\n                <nz-option\n                  nzCustomContent\n                  *ngFor=\"\n                    let item of periodTypeValue === 'rel'\n                      ? relativePeriodToBasisOptionList\n                      : absolutePeriodToBasisOptionList\n                  \"\n                  [nzValue]=\"item.name\"\n                  [nzLabel]=\"item.formattedLabel\"\n                >\n                  <va-icon *ngIf=\"periodToBasisOptionValue === item.name\" size=\"small\" name=\"check\"></va-icon>\n                  {{item.formattedLabel}}\n                </nz-option>\n              </nz-select>\n              <div class=\" period-filter-value-label\" *ngIf=\"hasOnlyYearBasis\">\n                {{ basisLabel }}\n              </div>\n            </div>\n          </div>\n          <div\n            class=\"period-filter-from-to-block period-filter-from-to-block-right\"\n          >\n            <div class=\"period-filter-from-to-label\">Year</div>\n            <div>\n              <nz-input-number\n                [nzMin]=\"\n                  periodFrom === '' || periodFrom === undefined || periodFrom === null\n                    ? minValue\n                    : periodFrom\n                \"\n                [nzMax]=\"maxValue\"\n                [(ngModel)]=\"periodTo\"\n                [nzPrecision]=\"0\"\n                class=\"val-period-year-selector\"\n                [ngClass]=\"{ 'period-year--error': isPeriodToYearInvalid }\"\n                (ngModelChange)=\"handlePeriodToValueChange($event)\"\n              ></nz-input-number>\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div\n      class=\"period-filter-info-holder mb-16\"\n      *ngIf=\"dataInfo && dataInfo.to && dataInfo.from\"\n    >\n      Available: {{basisLabel}} {{dataInfo.from}} to {{basisLabel}} {{dataInfo.to}}\n    </div>\n    <div class=\"period-filter-e-msg-holder mb-16\" *ngIf=\"errorMessage\">\n      <va-icon\n        size=\"small\"\n        name=\"exclamation-triangle\"\n        class=\"period-filter-e-info-icon\"\n      ></va-icon>\n      <div class=\"period-filter-error-msg\">&nbsp;{{ errorMessage }}</div>\n    </div>\n  </div>\n  <div\n    class=\"other-period-container\"\n    *ngIf=\"\n      periodData && periodData.other_period && periodData.other_period.length\n    \"\n  >\n    <nz-checkbox-wrapper\n      (nzOnChange)=\"handleOtherItemValueChange($event)\"\n      *ngIf=\"isMultiSelectPeriodFilter\"\n    >\n      <div\n        *ngFor=\"let item of periodData.other_period\"\n        class=\"val-period-checkbox\"\n      >\n        <label nz-checkbox [nzValue]=\"item.name\" [ngModel]=\"item.selected\">{{\n          item.label\n        }}</label>\n        <div\n          *ngIf=\"item.name === 'stm'\"\n          class=\"ca-tooltip-info-wrapper\"\n          nz-tooltip\n          nzTooltipPlacement=\"bottom\"\n          [nzTooltipTitle]=\"stmInfoTemplate\"\n        >\n          <va-icon size=\"small\" name=\"info-circle\"></va-icon>\n        </div>\n      </div>\n    </nz-checkbox-wrapper>\n    <nz-radio-group\n      [(ngModel)]=\"otherPeriodRadioValue\"\n      (ngModelChange)=\"handleOtherItemRadioValueChange($event)\"\n      *ngIf=\"!isMultiSelectPeriodFilter\"\n    >\n      <div\n        *ngFor=\"let item of periodData.other_period\"\n        class=\"val-period-checkbox\"\n      >\n        <label nz-radio [nzValue]=\"item.name\">{{ item.label }}</label>\n        <div\n          *ngIf=\"item.name === 'stm'\"\n          class=\"ca-tooltip-info-wrapper\"\n          nz-tooltip\n          nzTooltipPlacement=\"bottom\"\n          [nzTooltipTitle]=\"stmInfoTemplate\"\n        >\n          <va-icon size=\"small\" name=\"info-circle\"></va-icon>\n        </div>\n      </div>\n    </nz-radio-group>\n  </div>\n\n  <div class=\"period-filter-footer\">\n    <va-button\n      *ngIf=\"showApplyBtn\"\n      class=\"period-filter-button\"\n      btnType=\"solid\"\n      size=\"medium\"\n      [isDisabled]=\"\n        errorMessage || isPeriodToYearInvalid || isPeriodFromYearInvalid\n      \"\n      (onClick)=\"handleApply()\"\n    >\n      Apply\n    </va-button>\n  </div>\n</div>\n\n<ng-template #stmInfoTemplate>\n  <div class=\"ca-tooltip-content\">\n    <span> <va-icon size=\"small\" name=\"info-circle\"></va-icon></span>\n    &nbsp;\"Second Twelve Months (STM) uses the estimates for the 12 months\n    following the period in NTM.\"\n  </div>\n</ng-template>\n", styles: [".period-filter-radio-container{display:flex}.period-filter-radio-box{width:70px}.period-filter-content{padding:16px 20px}.period-filter-content-label{color:#222;font-size:14px;font-weight:600}.period-filter-footer{border-top:1px solid #d8d8d8;padding-top:12px;display:flex;flex-direction:column}.mb-16{margin-bottom:16px}.mb-12{margin-bottom:12px}.mr-40{margin-right:40px}.mb-8{margin-bottom:8px}.mb-4{margin-bottom:4px}::ng-deep .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner{border-color:#36abad}::ng-deep .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner:after{background-color:#36abad}::ng-deep .ant-radio:hover .ant-radio-inner{border-color:#36abad}::ng-deep .ant-checkbox-checked .ant-checkbox-inner{background-color:#36abad;border-color:#36abad}::ng-deep .ant-checkbox:hover .ant-checkbox-inner{border-color:#36abad}.ant-btn-primary{background-color:#36abad;border-color:#36abad}::ng-deep .period-filter-overlay{min-width:400px}::ng-deep .period-filter-overlay .ant-popover-inner-content{padding:0}::ng-deep .val-period-year-selector{width:80px}::ng-deep .val-period-year-selector.period-year--error{border-color:#e61f00}::ng-deep .val-period-year-selector.period-year--error:focus,::ng-deep .val-period-year-selector.period-year--error:hover{border-color:#e61f00}::ng-deep .val-period-year-selector .ant-input-number-handler-wrap{opacity:1}::ng-deep .val-period-year-selector:focus,::ng-deep .val-period-year-selector:hover{border-color:#36abad;box-shadow:none}::ng-deep .val-period-year-selector .ant-input-number-handler-down-inner,::ng-deep .val-period-year-selector .ant-input-number-handler-up-inner{color:#454545}::ng-deep .val-period-year-selector .ant-input-number-handler-down-inner:hover,::ng-deep .val-period-year-selector .ant-input-number-handler-up-inner:hover{color:#36abad}::ng-deep .val-period-year-selector .ant-input-number-handler:hover{color:#36abad}.val-period-checkbox{display:inline-flex;margin-right:16px}.period-filter-from-to-item{width:48%}.period-filter-from-to-wrapper{display:flex;justify-content:space-between}.period-filter-from-to-label{color:#979797;font-size:14px;margin-bottom:2px}.period-filter-from-to-block-wrapper{display:flex}.period-filter-from-to-block{display:inline-block}.period-filter-from-to-block:not(:last-child){margin-right:12px}.period-filter-from-to-block ::ng-deep .ant-select .ant-select-selector{padding:0 8px}.period-filter-from-to-block ::ng-deep .ant-select .ant-select-selector .ant-select-selection-item{padding-right:8px}.period-filter-from-to-block-right{flex:1}.period-filter-value-label{color:#222;font-size:14px;text-align:center;min-height:32px;align-items:center;justify-content:center;display:flex}::ng-deep .period-filter-basis-select{width:68px}::ng-deep .period-filter-basis-select .ant-select-selection:focus,::ng-deep .period-filter-basis-select .ant-select-selection:hover{border-color:#36abad;box-shadow:none}::ng-deep .period-filter-basis-select.ant-select-open .ant-select-arrow-icon{color:#36abad}::ng-deep .period-filter-basis-select .ant-select-arrow{right:8px}::ng-deep .period-filter-basis-select .ant-select-selection--single .ant-select-selection__rendered{margin-right:16px;margin-left:8px}::ng-deep .period-filter-basis-dropdown{min-width:98px}::ng-deep .period-filter-basis-dropdown .ant-select-dropdown::-webkit-scrollbar,::ng-deep .period-filter-basis-dropdown .cdk-virtual-scroll-viewport::-webkit-scrollbar{width:6px!important;height:6px!important}::ng-deep .period-filter-basis-dropdown .ant-select-dropdown::-webkit-scrollbar-track,::ng-deep .period-filter-basis-dropdown .cdk-virtual-scroll-viewport::-webkit-scrollbar-track{background:#c5ccd6;border-left:2px solid white;border-right:2px solid white}::ng-deep .period-filter-basis-dropdown .ant-select-dropdown::-webkit-scrollbar-thumb,::ng-deep .period-filter-basis-dropdown .cdk-virtual-scroll-viewport::-webkit-scrollbar-thumb{background-color:#36abad;border:none;border-radius:4px}::ng-deep .period-filter-basis-dropdown .ant-select-item{border-left:3px solid transparent;position:relative;padding-left:24px}::ng-deep .period-filter-basis-dropdown .ant-select-item:hover{border-color:#36abad}::ng-deep .period-filter-basis-dropdown .ant-select-item-option-selected{padding-left:4px}::ng-deep .period-filter-basis-dropdown .ant-select-item-option-selected va-icon{padding-right:2px;color:#36abad;font-size:14px}.period-filter-error-msg{color:#ca0000;font-size:11px}.period-filter-e-info-icon{color:#ca0000;font-size:14px;display:block}.period-filter-e-msg-holder{display:flex;align-items:center}.period-annual-wrapper{margin-left:24px}.period-annual-wrapper.period-annual-wrapper--disabled{opacity:.4;pointer-events:none}.period-filter-button{align-self:flex-end}.other-period-container{border-top:1px solid #d8d8d8;padding-top:12px;padding-bottom:12px}.period-filter-info-holder{color:#667284;font-size:12px;font-weight:400}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.VAIconRegistryService }]; }, propDecorators: { periodData: [{
                type: Input
            }], periodMinMax: [{
                type: Input
            }], hidePeriodType: [{
                type: Input
            }], hidePeriodBasis: [{
                type: Input
            }], showApplyBtn: [{
                type: Input
            }], dataAvailable: [{
                type: Input
            }], onApply: [{
                type: Output
            }], onError: [{
                type: Output
            }] } });

/**
 * This component represents "period filter with popup"
 * This component allows user to select period type, basis, from-to year range and other period(NTM, STM, LTM)
 *
 */
class PeriodFilterComponent {
    constructor() {
        this.disabled = false;
        this.className = "";
        this.placeholder = "Select period";
        this.periodData = DEFAULT_SINGLE_PERIOD_FILTER_DATA;
        this.periodMinMax = { min: REL_MIN_YEAR_RANGE, max: REL_MAX_YEAR_RANGE };
        this.popoverPlacement = "bottomLeft";
        this.onApply = new EventEmitter();
        this.hidePeriodType = false;
        this.hidePeriodBasis = false;
        // data
        this.isPopoverVisible = false;
    }
    ngOnChanges(changes) {
        if (changes.periodData &&
            changes.periodData.currentValue &&
            changes.periodData.currentValue !== changes.periodData.previousValue) {
            // set model variables
            if (this.periodData.type &&
                this.periodData.basis &&
                this.periodData.from_to) {
                this.setPeriodFilterLabel();
            }
        }
    }
    /**
     * It creates the label of selected values from period data
     */
    setPeriodFilterLabel() {
        this.selectedItem = createSelectedPeriodLabel(this.periodData);
    }
    /**
     * Executes on click of apply button
     * Emits `onApply` with updated periodData state and selected userSetting object
     * Hides the popup
     */
    handleOnApply(value) {
        this.onApply.emit(value);
        if (this.isPopoverVisible) {
            this.isPopoverVisible = false;
        }
    }
}
PeriodFilterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PeriodFilterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: PeriodFilterComponent, selector: "va-period-filter", inputs: { disabled: "disabled", className: "className", placeholder: "placeholder", periodData: "periodData", periodMinMax: "periodMinMax", popoverPlacement: "popoverPlacement", filterWidthVariation: "filterWidthVariation", hidePeriodType: "hidePeriodType", hidePeriodBasis: "hidePeriodBasis", dataAvailable: "dataAvailable" }, outputs: { onApply: "onApply" }, usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"period-filter-container\"\n  [ngClass]=\"[\n    isPopoverVisible ? 'period-filter--active' : '',\n    className,\n    disabled ? 'period-filter--disable' : '',\n    filterWidthVariation === 1\n      ? 'period-filter-container-width-one'\n      : filterWidthVariation === 2\n      ? 'period-filter-container-width-two'\n      : filterWidthVariation === 3\n      ? 'period-filter-container-width-three'\n      : 'period-filter-container-width-default'\n  ]\"\n>\n  <div class=\"period-filter-label\">Period</div>\n  <div\n    class=\"period-filter-select-container\"\n    nz-popover\n    nzPopoverOverlayClassName=\"period-filter-overlay\"\n    [nzPopoverPlacement]=\"popoverPlacement\"\n    [(nzPopoverVisible)]=\"isPopoverVisible\"\n    nzPopoverTrigger=\"click\"\n    [nzPopoverContent]=\"contentTemplate\"\n    [nzPopoverBackdrop]=\"true\"\n  >\n    <p class=\"period-filter-placeholder\" *ngIf=\"!selectedItem\">\n      {{ placeholder }}\n    </p>\n    <p\n      class=\"period-filter-selected-value\"\n      *ngIf=\"selectedItem\"\n      [title]=\"selectedItem\"\n    >\n      {{ selectedItem }}\n    </p>\n    <div class=\"period-filter-select-icon-wrapper\">\n      <i\n        nz-icon\n        [nzType]=\"isPopoverVisible ? 'up' : 'down'\"\n        class=\"angle-icon\"\n      ></i>\n    </div>\n  </div>\n  <ng-template #contentTemplate>\n    <va-period-filter-content\n      [periodData]=\"periodData\"\n      [periodMinMax]=\"periodMinMax\"\n      [hidePeriodType]=\"hidePeriodType\"\n      [hidePeriodBasis]=\"hidePeriodBasis\"\n      [dataAvailable]=\"dataAvailable\"\n      (onApply)=\"handleOnApply($event)\"\n    ></va-period-filter-content>\n  </ng-template>\n</div>\n", styles: [".period-filter-container-width-one{width:104px}.period-filter-container-width-two{width:186px}.period-filter-container-width-three{width:216px}.period-filter-container-width-default{width:100%}.period-filter-label{color:#979797;font-size:12px;line-height:1.4}.period-filter-select-container{position:relative;margin-top:4px;border-bottom:1px solid #c5ccd6;min-height:24px;cursor:pointer;padding-right:18px}.period-filter-selected-value{color:#222;font-size:14px;line-height:1.25;margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.period-filter-placeholder{color:#c5ccd6;font-size:14px;line-height:1.25;margin:0}.period-filter-select-icon-wrapper{position:absolute;right:0;top:50%;transform:translateY(-50%)}.period-filter-select-icon-wrapper .angle-icon{font-size:12px}.angle-icon{font-size:14px;color:#222;line-height:24px}.period-filter--active .period-filter-select-container{border-color:#36abad}.period-filter--active .angle-icon{color:#36abad}.period-filter--disable .period-filter-select-container{background-color:#f5f5f5;pointer-events:none}.period-filter--disable .period-filter-selected-value{color:#c5ccd6}::ng-deep .period-filter-overlay .ant-popover-arrow{display:none}::ng-deep .period-filter-overlay.ant-popover-placement-bottom,::ng-deep .period-filter-overlay.ant-popover-placement-bottomLeft,::ng-deep .period-filter-overlay.ant-popover-placement-bottomRight{padding-top:4px}::ng-deep .period-filter-overlay.ant-popover-inner{box-shadow:0 3px 6px -4px #0000001f,0 6px 16px #00000014,0 9px 28px 8px #0000000d}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$1.NzPopoverDirective, selector: "[nz-popover]", inputs: ["nzPopoverArrowPointAtCenter", "nzPopoverTitle", "nzPopoverContent", "nz-popover", "nzPopoverTrigger", "nzPopoverPlacement", "nzPopoverOrigin", "nzPopoverVisible", "nzPopoverMouseEnterDelay", "nzPopoverMouseLeaveDelay", "nzPopoverOverlayClassName", "nzPopoverOverlayStyle", "nzPopoverBackdrop"], outputs: ["nzPopoverVisibleChange"], exportAs: ["nzPopover"] }, { kind: "directive", type: i3$1.NzIconDirective, selector: "[nz-icon]", inputs: ["nzSpin", "nzRotate", "nzType", "nzTheme", "nzTwotoneColor", "nzIconfont"], exportAs: ["nzIcon"] }, { kind: "directive", type: i4$1.ɵNzTransitionPatchDirective, selector: "[nz-button], nz-button-group, [nz-icon], [nz-menu-item], [nz-submenu], nz-select-top-control, nz-select-placeholder, nz-input-group", inputs: ["hidden"] }, { kind: "component", type: PeriodFilterContentComponent, selector: "va-period-filter-content", inputs: ["periodData", "periodMinMax", "hidePeriodType", "hidePeriodBasis", "showApplyBtn", "dataAvailable"], outputs: ["onApply", "onError"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterComponent, decorators: [{
            type: Component,
            args: [{ selector: "va-period-filter", template: "<div\n  class=\"period-filter-container\"\n  [ngClass]=\"[\n    isPopoverVisible ? 'period-filter--active' : '',\n    className,\n    disabled ? 'period-filter--disable' : '',\n    filterWidthVariation === 1\n      ? 'period-filter-container-width-one'\n      : filterWidthVariation === 2\n      ? 'period-filter-container-width-two'\n      : filterWidthVariation === 3\n      ? 'period-filter-container-width-three'\n      : 'period-filter-container-width-default'\n  ]\"\n>\n  <div class=\"period-filter-label\">Period</div>\n  <div\n    class=\"period-filter-select-container\"\n    nz-popover\n    nzPopoverOverlayClassName=\"period-filter-overlay\"\n    [nzPopoverPlacement]=\"popoverPlacement\"\n    [(nzPopoverVisible)]=\"isPopoverVisible\"\n    nzPopoverTrigger=\"click\"\n    [nzPopoverContent]=\"contentTemplate\"\n    [nzPopoverBackdrop]=\"true\"\n  >\n    <p class=\"period-filter-placeholder\" *ngIf=\"!selectedItem\">\n      {{ placeholder }}\n    </p>\n    <p\n      class=\"period-filter-selected-value\"\n      *ngIf=\"selectedItem\"\n      [title]=\"selectedItem\"\n    >\n      {{ selectedItem }}\n    </p>\n    <div class=\"period-filter-select-icon-wrapper\">\n      <i\n        nz-icon\n        [nzType]=\"isPopoverVisible ? 'up' : 'down'\"\n        class=\"angle-icon\"\n      ></i>\n    </div>\n  </div>\n  <ng-template #contentTemplate>\n    <va-period-filter-content\n      [periodData]=\"periodData\"\n      [periodMinMax]=\"periodMinMax\"\n      [hidePeriodType]=\"hidePeriodType\"\n      [hidePeriodBasis]=\"hidePeriodBasis\"\n      [dataAvailable]=\"dataAvailable\"\n      (onApply)=\"handleOnApply($event)\"\n    ></va-period-filter-content>\n  </ng-template>\n</div>\n", styles: [".period-filter-container-width-one{width:104px}.period-filter-container-width-two{width:186px}.period-filter-container-width-three{width:216px}.period-filter-container-width-default{width:100%}.period-filter-label{color:#979797;font-size:12px;line-height:1.4}.period-filter-select-container{position:relative;margin-top:4px;border-bottom:1px solid #c5ccd6;min-height:24px;cursor:pointer;padding-right:18px}.period-filter-selected-value{color:#222;font-size:14px;line-height:1.25;margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.period-filter-placeholder{color:#c5ccd6;font-size:14px;line-height:1.25;margin:0}.period-filter-select-icon-wrapper{position:absolute;right:0;top:50%;transform:translateY(-50%)}.period-filter-select-icon-wrapper .angle-icon{font-size:12px}.angle-icon{font-size:14px;color:#222;line-height:24px}.period-filter--active .period-filter-select-container{border-color:#36abad}.period-filter--active .angle-icon{color:#36abad}.period-filter--disable .period-filter-select-container{background-color:#f5f5f5;pointer-events:none}.period-filter--disable .period-filter-selected-value{color:#c5ccd6}::ng-deep .period-filter-overlay .ant-popover-arrow{display:none}::ng-deep .period-filter-overlay.ant-popover-placement-bottom,::ng-deep .period-filter-overlay.ant-popover-placement-bottomLeft,::ng-deep .period-filter-overlay.ant-popover-placement-bottomRight{padding-top:4px}::ng-deep .period-filter-overlay.ant-popover-inner{box-shadow:0 3px 6px -4px #0000001f,0 6px 16px #00000014,0 9px 28px 8px #0000000d}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], className: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], periodData: [{
                type: Input
            }], periodMinMax: [{
                type: Input
            }], popoverPlacement: [{
                type: Input
            }], onApply: [{
                type: Output
            }], filterWidthVariation: [{
                type: Input
            }], hidePeriodType: [{
                type: Input
            }], hidePeriodBasis: [{
                type: Input
            }], dataAvailable: [{
                type: Input
            }] } });

class PeriodFilterModule {
}
PeriodFilterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PeriodFilterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterModule, declarations: [PeriodFilterComponent, PeriodFilterContentComponent], imports: [CommonModule,
        NzCheckboxModule,
        NzPopoverModule,
        NzIconModule,
        NzRadioModule,
        NzSelectModule,
        NzInputModule,
        NzToolTipModule,
        NzButtonModule,
        FormsModule,
        NzInputNumberModule,
        FontAwesomeModule,
        IconsModule,
        ButtonModule], exports: [PeriodFilterComponent, PeriodFilterContentComponent] });
PeriodFilterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterModule, imports: [CommonModule,
        NzCheckboxModule,
        NzPopoverModule,
        NzIconModule,
        NzRadioModule,
        NzSelectModule,
        NzInputModule,
        NzToolTipModule,
        NzButtonModule,
        FormsModule,
        NzInputNumberModule,
        FontAwesomeModule,
        IconsModule,
        ButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PeriodFilterModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PeriodFilterComponent, PeriodFilterContentComponent],
                    imports: [
                        CommonModule,
                        NzCheckboxModule,
                        NzPopoverModule,
                        NzIconModule,
                        NzRadioModule,
                        NzSelectModule,
                        NzInputModule,
                        NzToolTipModule,
                        NzButtonModule,
                        FormsModule,
                        NzInputNumberModule,
                        FontAwesomeModule,
                        IconsModule,
                        ButtonModule
                    ],
                    bootstrap: [],
                    exports: [PeriodFilterComponent, PeriodFilterContentComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ABSOLUTE_BASIS_OPTION_SHORTNAME, ABS_PERIOD_TYPE, ACTUAL_CALENDAR_YEAR, ANNUAL_ID, CALENDAR_BASIS_LABEL, CURRENT_YEAR, CY_PERIOD_BASIS, DEFAULT_FROM_YEAR, DEFAULT_RANGE_PERIOD_FILTER_DATA, DEFAULT_SINGLE_PERIOD_FILTER_DATA, DEFAULT_TO_YEAR, DEFAULT_VALUATION_PERIOD_FILTER_DATA, ERROR_MESSAGE, FINANCIAL_BASIS_LABEL, FY_PERIOD_BASIS, MONTH_DATE_ABB_YEAR_FORMAT, PERIOD_FULL_YEAR_ID, PeriodFilterComponent, PeriodFilterContentComponent, PeriodFilterModule, RELATIVE_BASIS_OPTION_SHORTNAME, REL_BASE_YEAR, REL_MAX_YEAR_RANGE, REL_MIN_YEAR_RANGE, REL_PERIOD_TYPE, convertAbsToRel, convertPeriodMetaToUserSettings, convertRelToAbs, createSelectedPeriodLabel, formatAbsolutePeriodBasisOptionList, formatRelativePeriodBasisOptionList, getAbsoluteMinMaxValue, getBasisPrefix, getIsAbsolutePeriod, getIsAbsolutePeriodTypeId, getIsCalYearSelected, getIsCalendarBasisId, getIsMultiSelectPeriodFilter, getMinMAxYearRange, getNameArrAccordingToSelectedState, getPeriodMinMax, getRelativeMinMAxValue, getSelectedBasisOption, getSelectedBasisOptionId, getSelectedPeriodBasis, getSelectedPeriodType, isBasisFullYear, isBasisHalfYearly, isBasisQuarterly, isPeriodFromToBasisDifferent, periodYearValueExist };
